import React, { useEffect, useState, useRef } from 'react';
import '../App.css';
import { useLinkedScroll } from '../hooks/useLinkedScroll';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { adminApi, getSeries, getStudyInfo, getStudyOpenBundle, getStudyPriors, triggerStudyRenderWarmup, userPreferenceApi } from '../api';
import { loadStudyOpenMetadata, mergePolledSeriesRows, mergeStudyInfoForActiveStudy } from './studyOpenBundle';
import { createStudyRouteGenerationGuard } from './studyRouteGeneration';
import { useViewerPageCineLifecycle } from './viewerPageCineLifecycle';
import { buildFhirImagingStudyUrl, buildFhirPatientUrl, buildSmartLaunchUrl } from '../fhirSmartLinks';
import axios from 'axios';
import SeriesPanel from './SeriesPanel';
import { buildKeyObjectMark } from './keyObjectSeries';
import PriorsPanel, { buildPriorCompareOptions } from './PriorsPanel';
import DiagnosticViewportSlot, { KAIROS_VIEWPORT_SLOT_ISOLATION_V1 } from './DiagnosticViewportSlot';
import VRTViewport from './VRTViewport';
// WebGLViewport removed - zero-footprint compliance
import { WL_PRESETS } from './WLPresets';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import KeyboardShortcutEditorDialog from './KeyboardShortcutEditorDialog';
import ViewerToolLayoutEditorDialog from './ViewerToolLayoutEditorDialog';
import LayoutGridPicker from './LayoutGridPicker';
import DicomTagsDialog from './DicomTagsDialog';
import OverlayConfigDialog from './OverlayConfigDialog';
import HangingProtocolEditorDialog from './HangingProtocolEditorDialog';
import QcTicketDialog, { KAIROS_VIEWER_QC_TICKET } from './QcTicketDialog';
import { getSeriesIngestProgress, KAIROS_SERIES_INGEST_PROGRESS } from './seriesThumbnail';
import { getViewportLayoutIdentity } from './viewportLayoutIdentity';
import MultiMonitorDialog from './MultiMonitorDialog';
import NativeReportPanel from './NativeReportPanel';
import StudyPerformanceDiagnosticStrip from './StudyPerformanceDiagnosticStrip';
import StudyPerformanceStatusPill from './StudyPerformanceStatusPill';
import { createStudyPerformanceTracker, getBrowserPerformanceContext, observeBrowserPerformance } from '../studyPerformanceTelemetry';
import { createStudyInteractionPerformanceTracker } from '../studyInteractionPerformance';
import { loadOverlayConfig, normalizeOverlayConfig, saveOverlayConfig } from '../overlayConfig';
import { selectReportContextAfterTombstone, selectReportContextAfterUpdate } from './reportContextOwnership';
import {
  KEYBOARD_SHORTCUT_PREF_NAMESPACE,
  loadKeyboardShortcutPreferences,
  saveKeyboardShortcutPreferences,
  normalizeKeyboardShortcutPreferences,
  formatShortcutLabel,
  normalizeShortcutKey,
  resolveViewerShortcut,
  viewerShortcutLabel,
} from './viewerKeyboardShortcuts';
import { buildPriorCompareSearchParams, buildPriorCompareStudyUrl, parsePriorCompareContext } from './priorCompareContext';
import { loadHangingProtocolOverrides, saveHangingProtocolOverrides } from '../hangingProtocolOverrides';
import { ANNOTATION_APPEARANCE_PREF_NAMESPACE, loadAnnotationAppearance, saveAnnotationAppearance, normalizeAnnotationAppearance } from '../annotationAppearance';
import { WORKSPACE_PREF_NAMESPACE, WORKSPACE_PRESET_IDS, WORKSPACE_PRESETS, TOOL_COMPLETION_BEHAVIORS, applyWorkspacePreset, loadWorkspacePrefs, saveWorkspacePrefs, normalizeWorkspacePrefs, getEffectiveWorkspaceChromeVisibility } from '../viewerWorkspacePreferences';
import { TOOL_LAYOUT_PREF_NAMESPACE, getToolbarFontScale, loadToolLayoutPreferences, saveToolLayoutPreferences, normalizeToolLayoutPreferences, selectToolsForPlacement } from '../viewerToolLayoutPreferences';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MPRView from './MPRView';
import { toggleMaximizedViewport, isViewportVisibleInMaximizeMode, VIEWPORT_DOUBLE_CLICK_MAXIMIZE_MARKER } from './viewportMaximize';
import { createTriangulationSyncCommands } from './viewportTriangulation';
import { createViewportTriangulationDragChannel } from './viewportTriangulationDragChannel';
import { createViewportReferenceGeometryChannel } from './viewportReferenceGeometryChannel';
import { modalityOf, pickBestPetForMprBase, pickDefaultMprFusionPair, eligiblePetFusionSeries } from './fusionSeriesPairing';
import { PET_SLICE_THICKNESS_HELP_MARKER, PET_SLICE_THICKNESS_HELP_TEXT, PET_SLICE_THICKNESS_MARKER, PET_SLICE_THICKNESS_OPTIONS_MM } from './petSliceThickness';
import { COMMAND_DECK_STATUS_MARKER, COMMAND_DECK_STATUS_OVERFLOW_MARKER, COMMAND_DECK_CLINICAL_STATUS_MARKER, COMMAND_DECK_REPORT_CONTEXT_MARKER, COMMAND_DECK_SLICE_PROGRESS_MARKER, COMMAND_DECK_PROTOCOL_STATUS_MARKER, COMMAND_DECK_ZOOM_FIT_MARKER, COMMAND_DECK_WINDOW_LEVEL_MARKER, COMMAND_DECK_WINDOW_LEVEL_PRESET_MARKER, COMMAND_DECK_MPR_FUSION_MARKER, COMMAND_DECK_ACTIVE_SERIES_MARKER, COMMAND_DECK_FREEZE_MARKER, COMMAND_DECK_PLANE_MARKER, COMMAND_DECK_SLICE_THICKNESS_MARKER, COMMAND_DECK_SERIES_COUNT_MARKER, COMMAND_DECK_ANATOMY_MARKER, COMMAND_DECK_ACQUISITION_TIME_MARKER, COMMAND_DECK_STUDY_DATE_MARKER, COMMAND_DECK_STUDY_DESCRIPTION_MARKER, COMMAND_DECK_STUDY_TIME_MARKER, COMMAND_DECK_MODALITY_MARKER, COMMAND_DECK_PROTOCOL_NAME_MARKER, COMMAND_DECK_SERIES_NUMBER_MARKER, COMMAND_DECK_ACQUISITION_NUMBER_MARKER, COMMAND_DECK_PIXEL_SPACING_MARKER, COMMAND_DECK_MANUFACTURER_MARKER, COMMAND_DECK_MODEL_MARKER, COMMAND_DECK_INSTITUTION_MARKER, COMMAND_DECK_STATION_MARKER, COMMAND_DECK_CONTRAST_PHASE_MARKER, COMMAND_DECK_SEQUENCE_MARKER, COMMAND_DECK_RECON_KERNEL_MARKER, COMMAND_DECK_PATIENT_POSITION_MARKER, COMMAND_DECK_ACCESSION_MARKER, COMMAND_DECK_PATIENT_ID_MARKER, COMMAND_DECK_PATIENT_DEMOGRAPHICS_MARKER, describeAccessionForCommandDeck, describePatientIdForCommandDeck, describePatientDemographicsForCommandDeck, describeAcquisitionTimeForCommandDeck, describeStudyDateForCommandDeck, describeStudyTimeForCommandDeck, describeStudyDescriptionForCommandDeck, describeModalityForCommandDeck, describeProtocolNameForCommandDeck, describeSeriesNumberForCommandDeck, describeAcquisitionNumberForCommandDeck, describePixelSpacingForCommandDeck, describeManufacturerForCommandDeck, describeScannerModelForCommandDeck, describeInstitutionForCommandDeck, describeStationForCommandDeck, describeAnatomyForCommandDeck, describeActiveSeriesForCommandDeck, describeContrastPhaseForCommandDeck, describeReconKernelForCommandDeck, describePatientPositionForCommandDeck, describeSequenceForCommandDeck, describeViewportPlaneForCommandDeck, describeSliceThicknessForCommandDeck, describeSeriesCountForCommandDeck, describeMprFusionForCommandDeck, describeReportContextForCommandDeck, describeViewportSliceProgress, describeViewportZoomFit, describeViewportWindowLevel, describeViewportFreezeForCommandDeck, describeHangingProtocolForCommandDeck } from './commandDeckStatus';
import { isCommandDeckPlaceholder, COMMAND_DECK_PRESENTATION_MARKER } from './commandDeckPresentation';
import useHangingProtocol from '../useHangingProtocol';
import { WL } from '../hangingProtocols';
import { Box, Typography, ToggleButton, IconButton, Tooltip, Button, Select, Menu, MenuItem, FormControl, Slider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StraightenIcon from '@mui/icons-material/Straighten';
import BrushIcon from '@mui/icons-material/Brush';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../AuthContext';
import LinkIcon from '@mui/icons-material/Link';
import GridOnIcon from '@mui/icons-material/GridOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HubIcon from '@mui/icons-material/Hub';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import { createMonitorWindowHandle, getWorkspaceParams, launchSavedMonitorWorkspace, loadProfiles, parseLaunchIntent, resolveLaunchProfile, useMultiMonitorSync } from '../multiMonitor';
import { shouldPublishAcceptedLinkedAuthority, shouldPublishAcceptedViewportScroll, VIEWPORT_REQUEST_ORIGIN } from './viewportPublicationPolicy';
import { MIXED_VIEWPORT_LAYOUT_PRESETS, getViewportCellStyle, getViewportCount, getViewportGridStyle, normalizeViewportLayout } from '../viewportLayoutGeometry';
import { shouldPersistPreferenceDefault } from './preferenceDefaultPersistence';
const OVERLAY_PREF_NAMESPACE = 'viewer.overlay';
const HANGING_PROTOCOL_PREF_NAMESPACE = 'viewer.hanging_protocols';
const EMPTY_COMPARE_CELLS = [];
const VIEWPORT_LINKED_PROGRESS_IDLE_COMMIT_MS = 160;
const seriesPerformanceInstanceCount = (row) => Math.max(0, Number(
  row?.numberOfSeriesRelatedInstances ?? row?.numberOfInstances ?? row?.instanceCount ?? row?.numInstances ?? 0
) || 0);
const COMMAND_DECK_GROUP_SX = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 'var(--kairos-toolbar-group-gap, 3px)',
  rowGap: 'var(--kairos-toolbar-group-row-gap, 2.4px)',
  px: 'var(--kairos-toolbar-group-px, 4.4px)',
  py: 'var(--kairos-toolbar-group-py, 2.4px)',
  border: '1px solid rgba(110,239,255,0.12)',
  borderRadius: 1.5,
  background: 'linear-gradient(135deg, rgba(6,16,24,0.88), rgba(7,10,15,0.72))',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.018), 0 8px 18px rgba(0,0,0,0.22)',
};

const COMMAND_DECK_LABEL_SX = {
  fontSize: 'var(--kairos-toolbar-label-font-size, 8px)',
  color: 'rgba(110,239,255,0.58)',
  letterSpacing: 1.4,
  fontFamily: 'monospace',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  mr: 0.15,
};

const CommandDeckGroup = ({ label, children, sx = {} }) => (
  <Box
    data-command-deck-group={label}
    role="group"
    aria-label={label}
    sx={{ ...COMMAND_DECK_GROUP_SX, ...sx }}
  >
    <Typography sx={COMMAND_DECK_LABEL_SX}>{label}</Typography>
    {children}
  </Box>
);

const AdvancedOverflowMenu = ({ onOpenFhirPatient, patientDisabled, smartHref, onOpenHangingProtocols }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  return <>
    <Tooltip title="More integrations and configuration"><IconButton size="small" aria-label="More viewer actions" aria-haspopup="menu" aria-expanded={Boolean(anchorEl)} onClick={event => setAnchorEl(event.currentTarget)} sx={{ width: 'var(--kairos-toolbar-control-min-height, 24px)', height: 'var(--kairos-toolbar-control-min-height, 24px)', p: 0.25, color: '#78919c', border: '1px solid rgba(110,239,255,0.14)' }}><MoreVertIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} PaperProps={{ sx: { bgcolor: '#071117', color: '#d7fbff', border: '1px solid rgba(110,239,255,0.20)' } }}>
      <MenuItem disabled={patientDisabled} onClick={() => { onOpenFhirPatient(); setAnchorEl(null); }}><HubIcon sx={{ fontSize: 15, mr: 1, color: '#66bb6a' }} />FHIR Patient</MenuItem>
      <MenuItem component="a" href={smartHref} target="_blank" rel="noopener noreferrer" onClick={() => setAnchorEl(null)}><OpenInNewIcon sx={{ fontSize: 15, mr: 1, color: '#ffb74d' }} />Launch SMART App</MenuItem>
      <MenuItem onClick={() => { onOpenHangingProtocols(); setAnchorEl(null); }} aria-label="Edit hanging protocol preferences"><ViewComfyIcon sx={{ fontSize: 15, mr: 1, color: '#6eefff' }} />Hanging protocol editor</MenuItem>
    </Menu>
  </>;
};

const TOOL_CLUSTER_SX = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 'var(--kairos-toolbar-cluster-gap, 2px)',
  px: 'var(--kairos-toolbar-cluster-px, 2.8px)',
  py: 'var(--kairos-toolbar-cluster-py, 1.6px)',
  borderLeft: '1px solid rgba(110,239,255,0.12)',
};

const TOOL_CLUSTER_LABEL_SX = {
  fontSize: 'var(--kairos-toolbar-cluster-label-font-size, 7px)',
  color: 'rgba(215,251,255,0.38)',
  letterSpacing: 1.1,
  fontFamily: 'monospace',
  fontWeight: 800,
  mr: 0.25,
};

const TOOL_BUTTON_SX = {
  fontSize: 'var(--kairos-toolbar-font-size, 10px)',
  py: 'var(--kairos-toolbar-control-py, 2px)',
  px: 'var(--kairos-toolbar-tool-px, 5.2px)',
  color: '#667788',
  borderColor: '#2a2a2a',
  borderRadius: 1.25,
};

const TOOL_KEYCAP_SX = {
  ml: 0.55,
  minWidth: 15,
  height: 15,
  px: 0.35,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  border: '1px solid rgba(110,239,255,0.26)',
  background: 'linear-gradient(180deg, rgba(110,239,255,0.14), rgba(7,18,27,0.82))',
  color: 'rgba(215,251,255,0.82)',
  fontFamily: 'monospace',
  fontSize: 'var(--kairos-toolbar-keycap-font-size, 8px)',
  fontWeight: 900,
  lineHeight: 1,
  pointerEvents: 'none',
};

const isToolShortcutKey = (key) => Boolean(normalizeShortcutKey(key));
const formatToolShortcutLabel = (key) => formatShortcutLabel(key);
const KAIROS_VIEWER_PROFILE_SHORTCUTS_TOOLBAR_ENTRY = 'KAIROS_VIEWER_PROFILE_SHORTCUTS_TOOLBAR_ENTRY';

const TOOL_CLUSTERS = [
  {
    label: 'NAV',
    ariaLabel: 'Navigation tools',
    tools: [
      { value: 'scroll', label: 'SCROLL', title: 'Scroll/cine (left-drag or wheel)', selected: { bgcolor: '#003344', color: 'primary.main', borderColor: '#00bcd4' } },
      { value: 'wl', label: 'W/L', title: 'Window / Level (drag)', icon: <OpenWithIcon sx={{ fontSize: 13, mr: 0.5 }} />, selected: { bgcolor: '#003344', color: 'primary.main', borderColor: '#00bcd4' } },
      { value: 'select', label: 'SELECT', title: 'Select/move annotations; drag blue handles to resize/edit; Delete removes selected', selected: { bgcolor: 'rgba(0,188,212,0.10)', color: '#00e5ff', borderColor: '#00e5ff' } },
    ],
  },
  {
    label: 'MEASURE',
    ariaLabel: 'Measurement tools',
    tools: [
      { value: 'measure', label: 'MEASURE', title: 'Measure (click two points)', icon: <StraightenIcon sx={{ fontSize: 13, mr: 0.5 }} />, selected: { bgcolor: '#332200', color: '#ffeb3b', borderColor: '#ffeb3b' } },
      { value: 'angle', label: 'ANGLE', title: '3-point angle measurement', selected: { bgcolor: 'rgba(0,188,212,0.10)', color: '#ffeb3b', borderColor: '#ffeb3b' } },
      { value: 'cobb', label: 'COBB', title: '4-point Cobb/spine angle', selected: { bgcolor: 'rgba(0,188,212,0.10)', color: '#66ff66', borderColor: '#66ff66' } },
    ],
  },
  {
    label: 'ROI',
    ariaLabel: 'ROI drawing tools',
    tools: [
      { value: 'roi', label: 'ROI', title: 'ROI - draw elliptical region of interest', icon: <BrushIcon sx={{ fontSize: 13, mr: 0.5 }} />, selected: { bgcolor: '#1a331a', color: '#4caf50', borderColor: '#4caf50' } },
      { value: 'rect', label: 'RECT', title: 'Rectangle annotation', selected: { bgcolor: 'rgba(0,188,212,0.10)', color: '#ffeb3b', borderColor: '#ffeb3b' } },
      { value: 'ellipse', label: 'ELLIPSE', title: 'Ellipse annotation', selected: { bgcolor: 'rgba(0,188,212,0.10)', color: '#66ff66', borderColor: '#66ff66' } },
    ],
  },
  {
    label: 'ANNOTATE',
    ariaLabel: 'Annotation tools',
    tools: [
      { value: 'arrow', label: 'ARROW', title: 'Arrow annotation (click tail, click head)', icon: <ArrowForwardIcon sx={{ fontSize: 13, mr: 0.5 }} />, selected: { bgcolor: '#332000', color: '#ff9800', borderColor: '#ff9800' } },
      { value: 'text', label: 'TEXT', title: 'Text label annotation (click image, type label)', icon: <TextFieldsIcon sx={{ fontSize: 13, mr: 0.5 }} />, selected: { bgcolor: '#2b1433', color: '#e040fb', borderColor: '#e040fb' } },
      { value: 'pen', label: 'PEN', title: 'Freehand pen annotation', selected: { bgcolor: 'rgba(0,188,212,0.10)', color: '#00e5ff', borderColor: '#00e5ff' } },
      { value: 'level', label: 'LEVEL', title: 'Vertebral level marker', selected: { bgcolor: 'rgba(0,188,212,0.10)', color: '#e040fb', borderColor: '#e040fb' } },
    ],
  },
];

const ToolCluster = ({ cluster, selectedTool, onSelectTool, toolShortcuts = {} }) => (
  <Box className="kairosToolCluster" data-tool-cluster={cluster.label} role="group" aria-label={cluster.ariaLabel} sx={TOOL_CLUSTER_SX}>
    <Typography sx={TOOL_CLUSTER_LABEL_SX}>{cluster.label}</Typography>
    {cluster.tools.map(tool => {
      const shortcutKey = isToolShortcutKey(toolShortcuts[tool.value]) ? toolShortcuts[tool.value] : '';
      const shortcutLabel = shortcutKey ? formatToolShortcutLabel(shortcutKey) : '';
      return (
        <ToggleButton
          key={tool.value}
          value={tool.value}
          selected={selectedTool === tool.value}
          onClick={() => onSelectTool(tool.value)}
          aria-label={shortcutLabel ? `${tool.title}; shortcut ${shortcutLabel}` : tool.title}
          aria-keyshortcuts={shortcutLabel || undefined}
          data-tool-shortcut={shortcutKey || undefined}
          sx={{
            ...TOOL_BUTTON_SX,
            '&.Mui-selected': tool.selected,
          }}
        >
          <Tooltip title={shortcutLabel ? `${tool.title} · shortcut ${shortcutLabel}` : tool.title}>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {tool.icon || null}{tool.label}
              {shortcutLabel && (
                <Box component="span" className="kairosToolShortcutKeycap" data-testid="tool-shortcut-keycap" aria-hidden="true" sx={TOOL_KEYCAP_SX}>
                  {shortcutLabel}
                </Box>
              )}
            </span>
          </Tooltip>
        </ToggleButton>
      );
    })}
  </Box>
);

const LAYOUTS = [
  ...Array.from({ length: 4 }, (_, rowIndex) =>
    Array.from({ length: 4 }, (_, colIndex) => ({
      label: `${rowIndex + 1}×${colIndex + 1}`,
      rows: rowIndex + 1,
      cols: colIndex + 1,
    }))
  ).flat(),
  MIXED_VIEWPORT_LAYOUT_PRESETS['2-over-3'],
  MIXED_VIEWPORT_LAYOUT_PRESETS['2x2-plus-1x3'],
].map(normalizeViewportLayout);


const ViewerPage = () => {
  const { studyInstanceUID } = useParams();
  const studyRouteGuardRef = useRef(null);
  if (!studyRouteGuardRef.current) {
    studyRouteGuardRef.current = createStudyRouteGenerationGuard(studyInstanceUID);
  }
  const studyRouteToken = studyRouteGuardRef.current.enter(studyInstanceUID);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, cacheEpoch, logout } = useAuth();
  const cineResourceGovernor = useViewerPageCineLifecycle({ logout, navigate });

  const handleLogout = async () => {
    cineResourceGovernor.reset('logout');
    await logout();
    navigate('/login', { replace: true });
  };
  React.useLayoutEffect(() => {
    cineResourceGovernor.reset('study-change');
  }, [cineResourceGovernor, studyInstanceUID]);
  React.useLayoutEffect(() => {
    cineResourceGovernor.reset('auth-scope-change');
  }, [cacheEpoch, cineResourceGovernor]);
  const comparisonContext = React.useMemo(() => parsePriorCompareContext(location.search), [location.search]);

  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState(null); // null = let hanging protocol decide
  const [manualLayout, setManualLayout] = useState(null); // user override
  const [layoutKey, setLayoutKey] = useState(0);
  const [preloaded, setPreloaded] = useState([]); // series pre-assigned to viewports
  const [slotWLs, setSlotWLs] = useState([]);   // per-viewport initial W/L from protocol
  const [slotLabels, setSlotLabels] = useState([]); // per-viewport slot labels
  const preloadedRef = useRef([]);
  const lastAppliedViewportLayoutRef = useRef(null);
  const { linkedScroll, linkedAuthorityEpoch, toggleLinkedScroll, onSliceChange: _onSliceChange, onAcceptedSliceChange: _onAcceptedSliceChange, linkedScrollFraction, linkedScrollCommandId, linkedScrollCommandChannel, resetLinkedScrollPositions } = useLinkedScroll();
  const [showRefLines, setShowRefLines] = React.useState(true);
  const [viewportSliceProgress, setViewportSliceProgress] = React.useState({});
  // Exact, synchronous slice-progress map. onSliceChange fires for EVERY
  // accepted slice from EVERY viewport, so with LINK ON a single wheel step
  // used to commit N page-level renders (source + every follower), each
  // re-rendering all mounted Viewports. The only consumers are the command-deck
  // slice readout (text) and the LINK toggle seed, so the render commit is
  // coalesced to one frame while this ref stays exact for the seed.
  const viewportSliceProgressRef = React.useRef({});
  const sliceProgressCommitFrameRef = React.useRef(null);
  const sliceProgressCommitTimerRef = React.useRef(null);
  const linkedScrollProgressModeRef = React.useRef(linkedScroll);
  linkedScrollProgressModeRef.current = linkedScroll;
  const scheduleSliceProgressCommit = React.useCallback(() => {
    if (linkedScrollProgressModeRef.current) {
      if (sliceProgressCommitTimerRef.current != null) {
        window.clearTimeout(sliceProgressCommitTimerRef.current);
      }
      sliceProgressCommitTimerRef.current = window.setTimeout(() => {
        sliceProgressCommitTimerRef.current = null;
        setViewportSliceProgress(viewportSliceProgressRef.current);
      }, VIEWPORT_LINKED_PROGRESS_IDLE_COMMIT_MS);
      return;
    }
    if (sliceProgressCommitTimerRef.current != null) {
      window.clearTimeout(sliceProgressCommitTimerRef.current);
      sliceProgressCommitTimerRef.current = null;
    }
    if (sliceProgressCommitFrameRef.current != null) return;
    sliceProgressCommitFrameRef.current = window.requestAnimationFrame(() => {
      sliceProgressCommitFrameRef.current = null;
      setViewportSliceProgress(viewportSliceProgressRef.current);
    });
  }, []);
  React.useEffect(() => () => {
    if (sliceProgressCommitFrameRef.current != null) {
      window.cancelAnimationFrame(sliceProgressCommitFrameRef.current);
      sliceProgressCommitFrameRef.current = null;
    }
    if (sliceProgressCommitTimerRef.current != null) {
      window.clearTimeout(sliceProgressCommitTimerRef.current);
      sliceProgressCommitTimerRef.current = null;
    }
  }, []);
  const [viewportZoomFit, setViewportZoomFit] = React.useState({});
  const [viewportWindowLevels, setViewportWindowLevels] = React.useState({});
  const [viewportFreezeState, setViewportFreezeState] = React.useState({});
  const [maximizedViewportIndex, setMaximizedViewportIndex] = React.useState(null);
  const [triangulationPoint, setTriangulationPoint] = React.useState(null);
  const [triangulationViewportCommands, setTriangulationViewportCommands] = React.useState({});
  // Imperative per-frame drag delivery: pointer-drag frames bypass React state
  // entirely (page + all panes would re-render per frame otherwise) and reach
  // follower scheduling/overlays through this stable channel. Gesture anchors
  // and clears still commit the state above as the durable authority.
  const triangulationDragChannel = React.useMemo(() => createViewportTriangulationDragChannel(), []);
  // Durable geometry remains exact even while REF is hidden. Coalesced channel
  // notifications repaint only the memoized SVG ReferenceLineLayer in each pane;
  // they never commit ViewerPage state or re-render the full viewport tree.
  const referenceGeometryChannel = React.useMemo(() => createViewportReferenceGeometryChannel(), []);
  const viewportReferenceGeometryRef = React.useRef({});
  const referenceGeometryCommitFrameRef = React.useRef(null);
  // Unmount fence: a queued geometry notification must never outlive the page.
  React.useEffect(() => () => {
    if (referenceGeometryCommitFrameRef.current != null) {
      window.cancelAnimationFrame(referenceGeometryCommitFrameRef.current);
      referenceGeometryCommitFrameRef.current = null;
    }
  }, []);
  const [globalTool, setGlobalTool] = useState('scroll');
  const [vrtSlot, setVrtSlot] = useState(null);
  // ctxMenu moved to per-viewport Viewport.js
  const [showHelp, setShowHelp] = useState(false); // { x, y } for W/L right-click menu // 'wl' | 'measure'
  const [mprMode, setMprMode] = useState(false);
  const [activeSeriesId, setActiveSeriesId] = useState(null);
  const [fusionPetSeriesId, setFusionPetSeriesId] = useState('');
  const [fusionOpacity, setFusionOpacity] = useState(0.45);
  const [fusionColormap, setFusionColormap] = useState('hot');
  const [fusionPetSlabMm, setFusionPetSlabMm] = useState(1);
  const [windowWidth, setWindowWidth]   = useState(0);
  const [windowCenter, setWindowCenter] = useState(0);
  const [studyInfo, setStudyInfo] = useState(null);
  const [priors, setPriors] = useState([]);
  const [priorsLoading, setPriorsLoading] = useState(false);
  const [priorsError, setPriorsError] = useState(null);
  const [tagsSeries, setTagsSeries] = useState(null);
  const [overlayConfig, setOverlayConfig] = useState(() => loadOverlayConfig());
  const [overlayDialogOpen, setOverlayDialogOpen] = useState(false);
  const [hpEditorOpen, setHpEditorOpen] = useState(false);
  const [qcTicketOpen, setQcTicketOpen] = useState(false);
  const [multiMonitorOpen, setMultiMonitorOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(true);
  const [compareRouteNotice, setCompareRouteNotice] = useState('');
  const [activeReportContext, setActiveReportContext] = useState(null);
  const [activeReportViewportIndex, setActiveReportViewportIndex] = useState(0);

  const handleToggleLinkedScroll = React.useCallback(() => {
    // Seed from the exact ref, not the coalesced state: enabling LINK must
    // adopt the slice actually on screen, never one frame stale.
    const acceptedSource = viewportSliceProgressRef.current[activeReportViewportIndex];
    toggleLinkedScroll(!linkedScroll && acceptedSource ? {
      sourceViewportIdx: activeReportViewportIndex,
      newSlice: acceptedSource.slice,
      totalSlices: acceptedSource.total,
    } : null);
  }, [activeReportViewportIndex, linkedScroll, toggleLinkedScroll]);
  const activeReportViewportIndexRef = React.useRef(activeReportViewportIndex);
  activeReportViewportIndexRef.current = activeReportViewportIndex;
  const [keyObjects, setKeyObjects] = useState([]);
  const reportContextsRef = useRef({});
  const [hangingProtocolOverrides, setHangingProtocolOverrides] = useState(() => loadHangingProtocolOverrides());
  const [annotationAppearance, setAnnotationAppearance] = useState(() => loadAnnotationAppearance());
  const [workspacePrefs, setWorkspacePrefs] = useState(() => loadWorkspacePrefs());
  const [toolLayoutPrefs, setToolLayoutPrefs] = useState(() => loadToolLayoutPreferences());
  const topToolIds = React.useMemo(() => new Set(selectToolsForPlacement(toolLayoutPrefs, 'top').map(tool => tool.id)), [toolLayoutPrefs]);
  const [keyboardShortcutPrefs, setKeyboardShortcutPrefs] = useState(() => loadKeyboardShortcutPreferences());
  const keyboardToolShortcuts = React.useMemo(
    () => normalizeKeyboardShortcutPreferences(keyboardShortcutPrefs).toolShortcuts,
    [keyboardShortcutPrefs]
  );
  const [shortcutEditorOpen, setShortcutEditorOpen] = useState(false);
  const [toolLayoutEditorOpen, setToolLayoutEditorOpen] = useState(false);
  const workspaceParams = React.useMemo(() => getWorkspaceParams(), []);
  // Route identity is deliberately only a generation dependency; it never enters the PHI-safe tracker.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const performanceTracker = React.useMemo(() => createStudyPerformanceTracker(), [studyInstanceUID]);
  const performanceTrackerRef = React.useRef(performanceTracker);
  performanceTrackerRef.current = performanceTracker;
  // Interaction evidence outlives the one-shot open tracker but shares only its opaque run id.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const interactionTracker = React.useMemo(() => createStudyInteractionPerformanceTracker(), [studyInstanceUID]);
  const interactionTrackerRef = React.useRef(interactionTracker);
  interactionTrackerRef.current = interactionTracker;
  const interactionFlushTimerRef = React.useRef(null);
  const interactionSubmissionChainRef = React.useRef(Promise.resolve());
  const flushInteractionRef = React.useRef(() => Promise.resolve(null));
  const performanceSeriesRef = React.useRef([]);
  performanceSeriesRef.current = series;
  const performanceAcceptedViewportsRef = React.useRef(new Set());
  const performanceFinalizeTimerRef = React.useRef(null);
  const performanceObserverStopRef = React.useRef(() => {});
  const performanceSubmissionRef = React.useRef({ tracker: null, promise: null, sent: false, controller: null });
  const performanceLifecycleGenerationRef = React.useRef(0);
  const performanceClosedReportRef = React.useRef(null);
  const [performanceOpen, setPerformanceOpen] = React.useState(false);
  const [performanceReport, setPerformanceReport] = React.useState(null);
  const [performanceEventId, setPerformanceEventId] = React.useState('');
  const [performanceSubmitState, setPerformanceSubmitState] = React.useState('idle');
  const buildPerformanceReport = React.useCallback((tracker = performanceTrackerRef.current) => {
    const rows = performanceSeriesRef.current || [];
    const modality = modalityOf(rows[0]) || 'OT';
    const studyRole = workspaceParams.role === 'comparison' ? 'comparison' : workspaceParams.role === 'priors' ? 'prior' : 'primary';
    return tracker.snapshot({
      modality,
      studyRole,
      ...getBrowserPerformanceContext(),
    });
  }, [workspaceParams.role]);
  flushInteractionRef.current = ({
    final = false,
    tracker = interactionTrackerRef.current,
    performanceTracker: openTracker = performanceTrackerRef.current,
    submissionSession = performanceSubmissionRef.current,
  } = {}) => {
    if (tracker !== interactionTrackerRef.current && !final) return Promise.resolve(null);
    const hadEvidence = tracker.isDirty();
    if (final && hadEvidence) tracker.settle({ final: true });
    if (!hadEvidence || !submissionSession?.sent || !openTracker) return Promise.resolve(null);
    const clientRunId = openTracker.snapshot().client_run_id;
    const payload = tracker.payload({ clientRunId });
    const generation = performanceLifecycleGenerationRef.current;
    interactionSubmissionChainRef.current = interactionSubmissionChainRef.current
      .catch(() => null)
      .then(() => {
        if (!final && (tracker !== interactionTrackerRef.current || generation !== performanceLifecycleGenerationRef.current)) return null;
        return adminApi.submitStudyInteractionPerformance(payload).catch(() => null);
      });
    return interactionSubmissionChainRef.current;
  };
  const handleInteractionPerformanceEvent = React.useCallback((event = {}) => {
    const tracker = interactionTrackerRef.current;
    try {
      if (event.type === 'input') tracker.recordInput(event);
      else if (event.type === 'target') tracker.recordTarget(event);
      else if (event.type === 'cache') tracker.recordCache(event);
      else if (event.type === 'accepted') tracker.recordAccepted(event);
      else return;
      if (interactionFlushTimerRef.current != null) window.clearTimeout(interactionFlushTimerRef.current);
      interactionFlushTimerRef.current = window.setTimeout(() => {
        interactionFlushTimerRef.current = null;
        flushInteractionRef.current({ tracker });
      }, 750);
    } catch (_) {
      // Monitoring must remain fail-open for clinical interaction.
    }
  }, []);
  const submitPerformanceReport = React.useCallback((report, tracker) => {
    if (!report || tracker !== performanceTrackerRef.current) return Promise.resolve(null);
    let session = performanceSubmissionRef.current;
    if (session.tracker !== tracker) {
      session.controller?.abort();
      session = { tracker, promise: null, sent: false, controller: null, generation: performanceLifecycleGenerationRef.current };
      performanceSubmissionRef.current = session;
    }
    if (session.sent) return Promise.resolve(null);
    if (session.promise) return session.promise;
    const { diagnosis: _localDiagnosis, ...payload } = report;
    session.controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    setPerformanceSubmitState('sending');
    session.promise = adminApi.submitStudyPerformance(payload, session.controller ? { signal: session.controller.signal } : {})
      .then((result) => {
        if (tracker !== performanceTrackerRef.current || performanceSubmissionRef.current !== session
          || session.generation !== performanceLifecycleGenerationRef.current) return null;
        session.sent = true;
        flushInteractionRef.current({ tracker: interactionTrackerRef.current });
        setPerformanceEventId(result?.event_id || '');
        setPerformanceSubmitState('sent');
        setPerformanceReport({ ...report, diagnosis: result?.diagnosis || report.diagnosis });
        return result;
      })
      .catch(() => {
        if (tracker === performanceTrackerRef.current && performanceSubmissionRef.current === session
          && session.generation === performanceLifecycleGenerationRef.current) {
          setPerformanceSubmitState('error');
        }
        return null;
      })
      .finally(() => {
        if (performanceSubmissionRef.current === session
          && session.generation === performanceLifecycleGenerationRef.current) session.promise = null;
      });
    return session.promise;
  }, []);
  const finalizePerformanceSession = React.useCallback((outcome = 'partial', tracker = performanceTrackerRef.current) => {
    if (tracker !== performanceTrackerRef.current) return null;
    if (performanceFinalizeTimerRef.current != null) {
      window.clearTimeout(performanceFinalizeTimerRef.current);
      performanceFinalizeTimerRef.current = null;
    }
    if (tracker.isClosed()) {
      const existing = performanceClosedReportRef.current;
      if (existing) submitPerformanceReport(existing, tracker);
      return existing;
    }
    performanceObserverStopRef.current();
    const expected = Math.max(1, preloadedRef.current.filter(Boolean).length);
    tracker.close({ outcome, expectedViewportCount: expected });
    const report = buildPerformanceReport(tracker);
    performanceClosedReportRef.current = report;
    setPerformanceReport(report);
    submitPerformanceReport(report, tracker);
    return report;
  }, [buildPerformanceReport, submitPerformanceReport]);
  React.useEffect(() => {
    const generation = ++performanceLifecycleGenerationRef.current;
    const capturedInteractionTracker = interactionTracker;
    const capturedPerformanceTracker = performanceTracker;
    performanceObserverStopRef.current();
    performanceSubmissionRef.current.controller?.abort();
    const capturedSubmissionSession = { tracker: performanceTracker, promise: null, sent: false, controller: null, generation };
    performanceSubmissionRef.current = capturedSubmissionSession;
    performanceClosedReportRef.current = null;
    const stop = observeBrowserPerformance(performanceTracker, { studyIdentity: studyInstanceUID });
    performanceObserverStopRef.current = stop;
    performanceFinalizeTimerRef.current = window.setTimeout(() => finalizePerformanceSession('timeout', performanceTracker), 10000);
    return () => {
      if (interactionFlushTimerRef.current != null) {
        window.clearTimeout(interactionFlushTimerRef.current);
        interactionFlushTimerRef.current = null;
      }
      flushInteractionRef.current({
        final: true,
        tracker: capturedInteractionTracker,
        performanceTracker: capturedPerformanceTracker,
        submissionSession: capturedSubmissionSession,
      });
      if (performanceLifecycleGenerationRef.current === generation) performanceLifecycleGenerationRef.current += 1;
      stop();
      if (performanceSubmissionRef.current.tracker === performanceTracker) {
        performanceSubmissionRef.current.controller?.abort();
        performanceSubmissionRef.current = { tracker: null, promise: null, sent: false, controller: null, generation: performanceLifecycleGenerationRef.current };
      }
      if (performanceFinalizeTimerRef.current != null) {
        window.clearTimeout(performanceFinalizeTimerRef.current);
        performanceFinalizeTimerRef.current = null;
      }
      if (performanceObserverStopRef.current === stop) performanceObserverStopRef.current = () => {};
    };
  }, [finalizePerformanceSession, interactionTracker, performanceTracker, studyInstanceUID]);
  React.useLayoutEffect(() => {
    performanceAcceptedViewportsRef.current = new Set();
    setPerformanceReport(null);
    setPerformanceEventId('');
    setPerformanceSubmitState('idle');
  }, [performanceTracker]);
  const [externalViewportCommands, setExternalViewportCommands] = React.useState({});
  const [keyboardViewportCommands, setKeyboardViewportCommands] = React.useState({});

  React.useLayoutEffect(() => {
    setLoading(true);
    setStudyInfo(null);
    setSeries([]);
    setPreloaded([]);
    preloadedRef.current = [];
    setSlotWLs([]);
    setSlotLabels([]);
    setActiveSeriesId(null);
    setFusionPetSeriesId('');
    setPriors([]);
    setPriorsError(null);
    setPriorsLoading(true);
    setTagsSeries(null);
    setActiveReportContext(null);
    setActiveReportViewportIndex(0);
    setKeyObjects([]);
    reportContextsRef.current = {};
    setViewportSliceProgress({});
    if (sliceProgressCommitTimerRef.current != null) {
      window.clearTimeout(sliceProgressCommitTimerRef.current);
      sliceProgressCommitTimerRef.current = null;
    }
    viewportSliceProgressRef.current = {};
    if (sliceProgressCommitFrameRef.current != null) {
      window.cancelAnimationFrame(sliceProgressCommitFrameRef.current);
      sliceProgressCommitFrameRef.current = null;
    }
    setViewportZoomFit({});
    setViewportWindowLevels({});
    setViewportFreezeState({});
    // Fence first, then synchronously clear every durable geometry consumer.
    if (referenceGeometryCommitFrameRef.current != null) {
      window.cancelAnimationFrame(referenceGeometryCommitFrameRef.current);
      referenceGeometryCommitFrameRef.current = null;
    }
    viewportReferenceGeometryRef.current = {};
    referenceGeometryChannel.reset();
    setTriangulationPoint(null);
    setTriangulationViewportCommands({});
    triangulationDragChannel.publish(null);
    setExternalViewportCommands({});
    setKeyboardViewportCommands({});
    setMaximizedViewportIndex(null);
    setVrtSlot(null);
    setMprMode(false);
    setCompareRouteNotice('');
  }, [referenceGeometryChannel, studyInstanceUID, triangulationDragChannel]);
  const { seriesRailVisible: effectiveSeriesRailVisible, reportRailVisible: effectiveReportRailVisible } = React.useMemo(
    () => getEffectiveWorkspaceChromeVisibility(workspacePrefs),
    [workspacePrefs]
  );
  const autoLaunchWorkspaceRef = React.useRef('');

  const handleMultiMonitorEvent = React.useCallback((envelope) => {
    const { type, payload } = envelope || {};
    if (!payload) return;
    if (type === 'active_study' && payload.studyInstanceUID && payload.studyInstanceUID !== studyInstanceUID) {
      navigate(`/study/${payload.studyInstanceUID}?workspace=${encodeURIComponent(workspaceParams.workspaceId)}&monitor=${encodeURIComponent(workspaceParams.monitorId)}&role=${encodeURIComponent(workspaceParams.role)}`);
      return;
    }
    if (['viewport_scroll', 'viewport_focus', 'series_loaded', 'window_level'].includes(type)) {
      const viewportIndex = Number(payload.viewportIndex || 0);
      if (type === 'window_level' && payload.wl) {
        setViewportWindowLevels(prev => ({ ...prev, [viewportIndex]: payload.wl }));
      }
      setExternalViewportCommands(prev => ({
        ...prev,
        [viewportIndex]: {
          ...payload,
          type,
          nonce: envelope.ts || Date.now(),
          requestOrigin: VIEWPORT_REQUEST_ORIGIN.MULTI_MONITOR_INBOUND,
        },
      }));
      if (type === 'viewport_scroll' && typeof payload.fraction === 'number') {
      }
    }
  }, [navigate, studyInstanceUID, workspaceParams.monitorId, workspaceParams.role, workspaceParams.workspaceId]);

  const { publish: publishMultiMonitor } = useMultiMonitorSync({ ...workspaceParams, onEvent: handleMultiMonitorEvent });

  const handleComparePriors = React.useCallback(async (selectedStudies = [], selectedCells = []) => {
    if (!selectedStudies.length) return;
    setCompareRouteNotice('');
    const total = selectedStudies.length;
    const optionsFor = (study, index) => {
      const base = buildPriorCompareOptions(study, index, total, selectedCells);
      return base.compare;
    };

    try {
      const loaded = await loadProfiles();
      const profile = resolveLaunchProfile(loaded, parseLaunchIntent(window.location.search));
      const priorsMonitor = (profile?.monitors || []).find(m => m.role === 'priors' && m.id !== workspaceParams.monitorId);
      if (priorsMonitor) {
        const targetIndex = Math.max(0, selectedStudies.findIndex(study => study.studyInstanceUID !== studyInstanceUID));
        const selectedIndex = targetIndex >= 0 ? targetIndex : 0;
        const targetStudy = selectedStudies[selectedIndex];
        const compare = optionsFor(targetStudy, selectedIndex);
        const reserved = createMonitorWindowHandle({ workspaceId: workspaceParams.workspaceId, monitorId: priorsMonitor.id, monitor: priorsMonitor });
        if (reserved.child && reserved.navigate(targetStudy.studyInstanceUID, 'priors', buildPriorCompareSearchParams(compare))) {
          setCompareRouteNotice(`Opened ${compare.label} on the saved priors monitor.`);
          return;
        }
      }
    } catch (e) {
      console.warn('Could not route prior comparison to saved priors monitor; falling back to tabs.');
    }

    const maxFallbackTabs = 8;
    const openedStudies = selectedStudies.slice(0, maxFallbackTabs);
    let blockedCount = 0;
    openedStudies.forEach((study, index) => {
      const child = window.open(buildPriorCompareStudyUrl(study.studyInstanceUID, optionsFor(study, index)), '_blank', 'noopener,noreferrer');
      if (!child) blockedCount += 1;
    });
    if (blockedCount === openedStudies.length) {
      setCompareRouteNotice('Browser blocked prior comparison tabs. Allow popups for this site or configure a saved priors monitor.');
    } else if (blockedCount > 0) {
      setCompareRouteNotice(`Some prior comparison tabs were blocked (${blockedCount}/${openedStudies.length}). Allow popups for this site.`);
    } else {
      setCompareRouteNotice(selectedStudies.length > maxFallbackTabs
        ? `No saved priors monitor was available; opened the first ${maxFallbackTabs} selected priors in tabs.`
        : 'No saved priors monitor was available; opened selected priors in tabs.');
    }
  }, [studyInstanceUID, workspaceParams.monitorId, workspaceParams.workspaceId]);

  React.useEffect(() => {
    if (!compareRouteNotice) return undefined;
    const timer = window.setTimeout(() => setCompareRouteNotice(''), 7000);
    return () => window.clearTimeout(timer);
  }, [compareRouteNotice]);

  React.useEffect(() => {
    setCompareRouteNotice('');
  }, [studyInstanceUID]);

  const resolveWorkspaceStudyForRole = React.useCallback(async (targetRole) => {
    if (targetRole !== 'priors') return { studyUID: studyInstanceUID, message: '' };
    try {
      const priorMatches = await getStudyPriors(studyInstanceUID, { limit: 1 });
      const prior = priorMatches?.[0];
      if (prior?.studyInstanceUID) {
        return { studyUID: prior.studyInstanceUID, message: 'Prior exam found and opened.' };
      }
      return { studyUID: null, message: 'No same-patient prior study was found for the prior monitor.' };
    } catch (e) {
      return { studyUID: null, message: `Could not check priors: ${e?.response?.data?.detail || e.message || 'unknown error'}` };
    }
  }, [studyInstanceUID]);

  React.useEffect(() => {
    publishMultiMonitor('active_study', { studyInstanceUID });
  }, [publishMultiMonitor, studyInstanceUID]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const launchIntent = parseLaunchIntent(window.location.search);
    if (!launchIntent.shouldLaunchWorkspace) return;
    const launchKey = `${studyInstanceUID}:${workspaceParams.workspaceId}:${launchIntent.profileId || 'active'}:${launchIntent.display || ''}`;
    if (autoLaunchWorkspaceRef.current === launchKey) return;
    autoLaunchWorkspaceRef.current = launchKey;

    let cancelled = false;
    (async () => {
      try {
        const loadedProfiles = await loadProfiles();
        if (cancelled) return;
        const activeProfile = resolveLaunchProfile(loadedProfiles, launchIntent);
        const result = await launchSavedMonitorWorkspace({
          profile: activeProfile ? { ...activeProfile, launchIntent } : activeProfile,
          studyInstanceUID,
          workspaceId: workspaceParams.workspaceId,
          monitorId: workspaceParams.monitorId,
          role: workspaceParams.role,
          resolveStudyForRole: resolveWorkspaceStudyForRole,
        });
        if (result.skipped?.length) console.warn('Workspace auto-launch skipped monitors:', result.skipped);

        params.delete('launchWorkspace');
        params.delete('autoLaunchWorkspace');
        params.delete('externalLaunch');
        const nextSearch = params.toString();
        const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
        window.history.replaceState(window.history.state, '', nextUrl);
      } catch (e) {
        console.warn('Workspace auto-launch failed', e);
      }
    })();

    return () => { cancelled = true; };
  }, [resolveWorkspaceStudyForRole, studyInstanceUID, workspaceParams.monitorId, workspaceParams.role, workspaceParams.workspaceId]);

  useEffect(() => {
    setViewportSliceProgress({});
    if (sliceProgressCommitTimerRef.current != null) {
      window.clearTimeout(sliceProgressCommitTimerRef.current);
      sliceProgressCommitTimerRef.current = null;
    }
    viewportSliceProgressRef.current = {};
    if (sliceProgressCommitFrameRef.current != null) {
      window.cancelAnimationFrame(sliceProgressCommitFrameRef.current);
      sliceProgressCommitFrameRef.current = null;
    }
    setViewportZoomFit({});
    setViewportWindowLevels({});
    setViewportFreezeState({});
    setMaximizedViewportIndex(null);
    setTriangulationViewportCommands({});
    triangulationDragChannel.publish(null);
    // Study switch fences the queued notify before clearing both exact consumers.
    if (referenceGeometryCommitFrameRef.current != null) {
      window.cancelAnimationFrame(referenceGeometryCommitFrameRef.current);
      referenceGeometryCommitFrameRef.current = null;
    }
    viewportReferenceGeometryRef.current = {};
    referenceGeometryChannel.reset();
    resetLinkedScrollPositions();
  }, [referenceGeometryChannel, resetLinkedScrollPositions, studyInstanceUID, triangulationDragChannel]);

  const handleViewportSeriesLoadedForLinkedScroll = React.useCallback((viewportIndex) => {
    // A series switch changes the target slice domain. Drop stale fractional
    // linked-scroll state so the new series does not chase an old viewport's
    // position and build a browser render-request backlog.
    resetLinkedScrollPositions();
  }, [resetLinkedScrollPositions]);

  const onLinkedSliceRequest = React.useCallback((vpIdx, slice, total, meta = null) => {
    if (meta?.source === 'linked_rollback' || meta?.source === 'linked_final') {
      return _onAcceptedSliceChange(vpIdx, slice, total, { force: true });
    }
    return _onSliceChange(vpIdx, slice, total);
  }, [_onAcceptedSliceChange, _onSliceChange]);

  const onSliceChange = React.useCallback((vpIdx, slice, total, meta = null) => {
    if (meta?.source === 'cleared' || !Number.isFinite(slice) || !Number.isFinite(total)) {
      // Series/study authority disappears synchronously, before asynchronous
      // frame-count resolution or onSeriesLoad. Fence every LINK follower now.
      if (meta?.source === 'cleared' && linkedScrollProgressModeRef.current) {
        resetLinkedScrollPositions();
      }
      if (Object.prototype.hasOwnProperty.call(viewportSliceProgressRef.current, vpIdx)) {
        const cleared = { ...viewportSliceProgressRef.current };
        delete cleared[vpIdx];
        viewportSliceProgressRef.current = cleared;
        scheduleSliceProgressCommit();
      }
      return null;
    }
    const fraction = total > 1 ? slice / (total - 1) : 0;
    // Clinical publication is authoritative and always precedes fail-open telemetry.
    viewportSliceProgressRef.current = { ...viewportSliceProgressRef.current, [vpIdx]: { slice, total } };
    scheduleSliceProgressCommit();
    const linkedCommandId = shouldPublishAcceptedLinkedAuthority(meta?.requestOrigin)
      ? _onAcceptedSliceChange(vpIdx, slice, total) : null;
    if (shouldPublishAcceptedViewportScroll(meta)) {
      publishMultiMonitor('viewport_scroll', { viewportIndex: vpIdx, slice, total, fraction, studyInstanceUID, source: meta?.source || 'accepted_visible' });
    }
    if (meta?.source === 'accepted_visible' && !performanceAcceptedViewportsRef.current.has(vpIdx)) {
      try {
        const rows = performanceSeriesRef.current || [];
        const visibleSeries = preloadedRef.current[vpIdx] || rows[vpIdx] || rows[0] || null;
        const seriesOrdinalIndex = visibleSeries?.seriesInstanceUID
          ? rows.findIndex(row => row.seriesInstanceUID === visibleSeries.seriesInstanceUID) : vpIdx;
        performanceTracker.markAcceptedVisible({
          viewportIndex: vpIdx,
          seriesOrdinal: Math.max(0, seriesOrdinalIndex) + 1,
          modality: modalityOf(visibleSeries) || 'OT',
          instanceCount: seriesPerformanceInstanceCount(visibleSeries) || total,
        });
        performanceAcceptedViewportsRef.current.add(vpIdx);
        const expectedVisible = Math.max(1, preloadedRef.current.filter(Boolean).length);
        if (performanceAcceptedViewportsRef.current.size >= expectedVisible) {
          performanceTracker.markInteractive();
          finalizePerformanceSession('complete', performanceTracker);
        }
      } catch (_) {
        // Telemetry must never interrupt accepted-pixel, LINK, or multi-monitor publication.
      }
    }
    return linkedCommandId;
  }, [_onAcceptedSliceChange, finalizePerformanceSession, performanceTracker, publishMultiMonitor, resetLinkedScrollPositions, scheduleSliceProgressCommit, studyInstanceUID]);

  const handleViewportGeometryChange = React.useCallback((vpIdx, payload) => {
    const current = viewportReferenceGeometryRef.current || {};
    let next;
    if (!payload) {
      if (!Object.prototype.hasOwnProperty.call(current, vpIdx)) return;
      next = { ...current };
      delete next[vpIdx];
    } else {
      next = { ...current, [vpIdx]: payload };
    }
    // Triangulation and late REF subscribers consume the exact snapshot immediately.
    viewportReferenceGeometryRef.current = next;
    referenceGeometryChannel.replaceSnapshot(next);
    if (!showRefLines) return;
    // REF ON: notify only the small SVG layers once per frame. No ViewerPage
    // state changes and no full diagnostic viewport receives a changing map prop.
    if (referenceGeometryCommitFrameRef.current != null) return;
    referenceGeometryCommitFrameRef.current = window.requestAnimationFrame(() => {
      referenceGeometryCommitFrameRef.current = null;
      referenceGeometryChannel.notify();
    });
  }, [referenceGeometryChannel, showRefLines]);

  const handleReportContextChange = React.useCallback((ctx) => {
    if (!ctx || ctx.viewportIndex == null) return;
    if (ctx.cleared) {
      const next = { ...reportContextsRef.current };
      delete next[ctx.viewportIndex];
      reportContextsRef.current = next;
      setActiveReportContext(prev => selectReportContextAfterTombstone({
        displayedContext: prev,
        remainingContexts: next,
        activeViewportIndex: activeReportViewportIndexRef.current,
        clearedViewportIndex: ctx.viewportIndex,
      }));
      return;
    }
    reportContextsRef.current = { ...reportContextsRef.current, [ctx.viewportIndex]: ctx };
    setActiveReportContext(prev => selectReportContextAfterUpdate({
      displayedContext: prev,
      incomingContext: ctx,
      activeViewportIndex: activeReportViewportIndexRef.current,
    }));
  }, []);

  const focusReportViewport = React.useCallback((viewportIndex) => {
    activeReportViewportIndexRef.current = viewportIndex;
    setActiveReportViewportIndex(viewportIndex);
    setActiveReportContext(reportContextsRef.current[viewportIndex] || null);
  }, []);

  const handleTriangulationPoint = React.useCallback((payload, meta = null) => {
    if (!payload) {
      triangulationDragChannel.publish(null);
      setTriangulationPoint(null);
      setTriangulationViewportCommands({});
      return;
    }
    const enriched = { ...payload, nonce: payload.timestamp || Date.now() };
    const commands = createTriangulationSyncCommands(enriched, viewportReferenceGeometryRef.current);
    // Every frame (transient drag or committed anchor) is delivered
    // imperatively so followers and overlays track the pointer at cadence.
    triangulationDragChannel.publish({ point: enriched, commands });
    if (meta?.transient) return;
    setTriangulationPoint(enriched);
    setTriangulationViewportCommands(commands);
  }, [triangulationDragChannel]);


  useEffect(() => {
    if (!user?.id && !user?.username) return;
    let cancelled = false;

    const preferenceSpecs = [
      { namespace: OVERLAY_PREF_NAMESPACE, localValue: loadOverlayConfig(), applyValue: setOverlayConfig, cacheValue: saveOverlayConfig, normalizeValue: normalizeOverlayConfig },
      {
        namespace: HANGING_PROTOCOL_PREF_NAMESPACE,
        localValue: loadHangingProtocolOverrides(),
        applyValue: setHangingProtocolOverrides,
        cacheValue: saveHangingProtocolOverrides,
        normalizeValue: value => Array.isArray(value) ? value : [],
      },
      { namespace: ANNOTATION_APPEARANCE_PREF_NAMESPACE, localValue: loadAnnotationAppearance(), applyValue: setAnnotationAppearance, cacheValue: saveAnnotationAppearance, normalizeValue: normalizeAnnotationAppearance },
      { namespace: KEYBOARD_SHORTCUT_PREF_NAMESPACE, localValue: loadKeyboardShortcutPreferences(), applyValue: setKeyboardShortcutPrefs, cacheValue: saveKeyboardShortcutPreferences, normalizeValue: normalizeKeyboardShortcutPreferences },
      { namespace: WORKSPACE_PREF_NAMESPACE, localValue: loadWorkspacePrefs(), applyValue: setWorkspacePrefs, cacheValue: saveWorkspacePrefs, normalizeValue: normalizeWorkspacePrefs },
      { namespace: TOOL_LAYOUT_PREF_NAMESPACE, localValue: loadToolLayoutPreferences(), applyValue: setToolLayoutPrefs, cacheValue: saveToolLayoutPreferences, normalizeValue: normalizeToolLayoutPreferences },
    ];

    const applyPreferenceValue = ({ normalizeValue, cacheValue, applyValue }, value) => {
      if (cancelled) return;
      const normalizedValue = normalizeValue(value);
      cacheValue(normalizedValue);
      applyValue(normalizedValue);
    };

    const savePreferenceDefault = async ({ namespace, localValue }) => {
      if (!shouldPersistPreferenceDefault(namespace, localValue)) return;
      try { await userPreferenceApi.save(namespace, localValue); }
      catch (saveError) { console.warn(`Could not migrate ${namespace} preference`, saveError); }
    };

    const loadPreferenceByGet = async (spec) => {
      try {
        const pref = await userPreferenceApi.get(spec.namespace);
        if (pref && Object.prototype.hasOwnProperty.call(pref, 'value')) {
          applyPreferenceValue(spec, pref.value);
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          await savePreferenceDefault(spec);
        } else {
          console.warn(`Could not load ${spec.namespace} preference; using localStorage fallback`, e);
        }
      }
    };

    const loadPreferences = async () => {
      try {
        const payload = await userPreferenceApi.list();
        const preferences = Array.isArray(payload) ? payload : (payload?.preferences || []);
        const prefMap = new Map(
          preferences
            .filter(pref => pref && typeof pref.namespace === 'string')
            .map(pref => [pref.namespace, pref])
        );
        for (const spec of preferenceSpecs) {
          if (cancelled) return;
          const pref = prefMap.get(spec.namespace);
          if (pref && Object.prototype.hasOwnProperty.call(pref, 'value')) {
            applyPreferenceValue(spec, pref.value);
          } else {
            await savePreferenceDefault(spec);
          }
        }
      } catch (e) {
        console.warn('Could not list viewer preferences; falling back to per-namespace preference loads', e);
        for (const spec of preferenceSpecs) {
          if (cancelled) return;
          await loadPreferenceByGet(spec);
        }
      }
    };

    loadPreferences();

    return () => { cancelled = true; };
  }, [user?.id, user?.username]);

  useEffect(() => {
    let cancelled = false;
    const isCurrentStudy = () => (
      !cancelled && studyRouteGuardRef.current.isCurrent(studyRouteToken)
    );
    setLoading(true);
    setPriors([]);
    setPriorsError(null);
    setPriorsLoading(true);

    triggerStudyRenderWarmup(studyInstanceUID).catch(e => console.warn('triggerStudyRenderWarmup failed:', e));
    getStudyInfo(studyInstanceUID).then(info => {
      if (!isCurrentStudy()) return;
      if (info) {
        setStudyInfo(previous => mergeStudyInfoForActiveStudy(previous, info, studyInstanceUID));
      }
      // Audit and DICOMweb institution enrichment intentionally remain independent
      // from the read-only bundle so failures cannot block diagnostic study-open.
      axios.post('/auth/audit/study-access', {
        study_uid: studyInstanceUID,
        series_uid: null,
        patient_id: info?.patientId || null,
        access_method: 'viewer_open',
      }, { withCredentials: true }).catch(e => console.warn('auditStudyAccess failed:', e));
    }).catch(error => {
      if (isCurrentStudy()) console.warn('getStudyInfo failed:', error);
    });

    const applySeriesData = (data, initial = false) => {
      if (!isCurrentStudy()) return;
      if (initial) setSeries(data);
      else setSeries(previous => mergePolledSeriesRows(previous, data));
      if (initial) {
        performanceSeriesRef.current = data;
        performanceTracker.markMetadataReady({
          seriesCount: data.length,
          instanceCount: data.reduce((sum, row) => sum + seriesPerformanceInstanceCount(row), 0),
        });
        setPerformanceReport(buildPerformanceReport());
      }
      setLoading(false);
      if (data.some(item => getSeriesIngestProgress(item).active)) {
        console.info(KAIROS_SERIES_INGEST_PROGRESS, 'active series ingest/processing detected');
      }
      if (initial) {
        // Pre-set active series for MPR and pair PET fusion by coverage/acquisition family.
        const { baseSeries, petSeries } = pickDefaultMprFusionPair(data);
        if (baseSeries) setActiveSeriesId(baseSeries.seriesInstanceUID);
        setFusionPetSeriesId(petSeries?.seriesInstanceUID || '');
      }
    };

    loadStudyOpenMetadata(studyInstanceUID, {
      getBundle: getStudyOpenBundle,
      getSeries,
      getPriors: getStudyPriors,
    })
      .then(payload => {
        if (!isCurrentStudy()) return;
        if (payload.study) {
          setStudyInfo(previous => mergeStudyInfoForActiveStudy(
            previous,
            payload.study,
            studyInstanceUID,
            { preserveExistingFields: ['facilityName'] },
          ));
        }
        applySeriesData(payload.series || [], true);
        setPriors(payload.priors || []);
        if (payload.priorsError) {
          setPriorsError(payload.priorsError?.response?.data?.detail || payload.priorsError.message || 'Unknown error');
        }
        setPriorsLoading(false);
      })
      .catch(error => {
        if (!isCurrentStudy()) return;
        setLoading(false);
        setPriorsLoading(false);
        setPriorsError(error?.response?.data?.detail || error.message || 'Unknown error');
      });

    const seriesRefreshTimer = window.setInterval(() => {
      getSeries(studyInstanceUID).then(data => applySeriesData(data, false)).catch(() => {});
    }, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(seriesRefreshTimer);
    };
  }, [buildPerformanceReport, performanceTracker, studyInstanceUID, studyRouteToken]);

  const applyViewportLayout = React.useCallback(({ nextLayout, nextPreloaded, nextSlotWLs, nextSlotLabels }, { force = false } = {}) => {
    const identity = getViewportLayoutIdentity({
      studyInstanceUID,
      role: workspaceParams.role,
      layout: nextLayout,
      preloaded: nextPreloaded,
      slotWLs: nextSlotWLs,
      slotLabels: nextSlotLabels,
    });

    if (!force && lastAppliedViewportLayoutRef.current === identity) {
      return false;
    }

    lastAppliedViewportLayoutRef.current = identity;
    setLayout(nextLayout);
    setLayoutKey(k => k + 1);
    setPreloaded(nextPreloaded);
    preloadedRef.current = nextPreloaded;
    setSlotWLs(nextSlotWLs);
    setSlotLabels(nextSlotLabels);
    performanceTracker.markLayoutReady({ viewportCount: nextPreloaded.filter(Boolean).length || getViewportCount(nextLayout) });
    setPerformanceReport(buildPerformanceReport());
    const protocolWindows = nextPreloaded.map((series, viewportIndex) => ({
      seriesInstanceUID: series?.seriesInstanceUID,
      ...nextSlotWLs[viewportIndex],
    })).filter(route => route.seriesInstanceUID && Number.isFinite(Number(route.ww)) && Number(route.ww) !== 0);
    if (protocolWindows.length) {
      triggerStudyRenderWarmup(studyInstanceUID, protocolWindows).catch(() => null);
    }
    return true;
  }, [buildPerformanceReport, performanceTracker, studyInstanceUID, workspaceParams.role]);

  // ── Hanging protocol ────────────────────────────────────────────────────
  const { protocol, layout: hpLayout, assignments } = useHangingProtocol(series, studyInfo, hangingProtocolOverrides);

  // Apply hanging protocol when series + studyInfo are ready (and no manual override yet).
  // QOL1 background ingest polling still refreshes the series rail, but repeated
  // metadata-only polls must not bump layoutKey and remount diagnostic viewports.
  useEffect(() => {
    if (!series.length || !hpLayout) return;
    if (manualLayout) return; // user already picked a layout manually
    if (workspaceParams.role === 'comparison') {
      const comparisonAssignment = assignments.find((a, idx) => idx > 0 && a.series)?.series;
      const comparisonSeries = comparisonAssignment || series[1] || series[0] || null;
      applyViewportLayout({
        nextLayout: LAYOUTS[0],
        nextPreloaded: [comparisonSeries],
        nextSlotWLs: [assignments[1]?.wl || assignments[0]?.wl || { ww: 0, wc: 0 }],
        nextSlotLabels: ['Comparison series'],
      });
      return;
    }
    if (workspaceParams.role === 'priors') {
      const primarySeries = assignments[0]?.series || series[0] || null;
      applyViewportLayout({
        nextLayout: LAYOUTS[0],
        nextPreloaded: [primarySeries],
        nextSlotWLs: [assignments[0]?.wl || { ww: 0, wc: 0 }],
        nextSlotLabels: ['Prior exam'],
      });
      return;
    }
    const matched = hpLayout.geometry
      ? normalizeViewportLayout(hpLayout)
      : (LAYOUTS.find(l => !l.geometry && l.rows === hpLayout.rows && l.cols === hpLayout.cols) || LAYOUTS[2]);
    applyViewportLayout({
      nextLayout: matched,
      nextPreloaded: assignments.map(a => a.series || null),
      nextSlotWLs: assignments.map(a => a.wl || { ww: 0, wc: 0 }),
      nextSlotLabels: assignments.map(a => a.label || ''),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, studyInfo, hpLayout, workspaceParams.role, applyViewportLayout]);

  const handleLayoutChange = (_, val) => {
    if (!val) return;
    const found = LAYOUTS.find(l => l.label === val);
    if (found) {
      setManualLayout(found);
      setLayout(found);
      setLayoutKey(k => k + 1);
      // Re-preload up to new viewport count using protocol assignments where possible
      const count = getViewportCount(found);
      const newPreloaded = Array.from({ length: count }, (_, i) =>
        assignments[i]?.series || series[i] || null
      );
      if (!performanceTracker.isClosed()) {
        finalizePerformanceSession(performanceAcceptedViewportsRef.current.size ? 'partial' : 'cancelled', performanceTracker);
      }
      performanceAcceptedViewportsRef.current = new Set();
      preloadedRef.current = newPreloaded;
      setPreloaded(newPreloaded);
      setSlotWLs(Array.from({ length: count }, (_, i) => assignments[i]?.wl || { ww: 0, wc: 0 }));
      setSlotLabels(Array.from({ length: count }, (_, i) => assignments[i]?.label || ''));
    }
  };

  const activeLayout = normalizeViewportLayout(layout || LAYOUTS[2]); // fallback while loading

  const numViewports = getViewportCount(activeLayout);

  React.useEffect(() => {
    if (maximizedViewportIndex !== null && maximizedViewportIndex >= numViewports) {
      setMaximizedViewportIndex(null);
    }
  }, [maximizedViewportIndex, numViewports]);

  const handleViewportDoubleClickMaximize = React.useCallback((viewportIndex, payload = {}) => {
    setMaximizedViewportIndex(prev => toggleMaximizedViewport(prev, viewportIndex, numViewports));
    focusReportViewport(viewportIndex);
    publishMultiMonitor('viewport_double_click_maximize', {
      viewportIndex,
      slice: payload?.slice ?? 0,
      marker: VIEWPORT_DOUBLE_CLICK_MAXIMIZE_MARKER,
    });
  }, [numViewports, focusReportViewport, publishMultiMonitor]);

  // ── Viewer-level keyboard shortcuts ─────────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && maximizedViewportIndex !== null) {
        e.preventDefault();
        setMaximizedViewportIndex(null);
        return;
      }
      const shortcut = resolveViewerShortcut(e, WL_PRESETS, keyboardToolShortcuts);
      if (!shortcut) return;
      e.preventDefault();
      if (shortcut.type === 'help') {
        setShowHelp(h => !h);
        return;
      }
      if (shortcut.type === 'tool') {
        if (shortcut.tool === 'freezeViewport') {
          const viewportIndex = Math.max(0, Math.min(numViewports - 1, activeReportViewportIndex || 0));
          const nonce = Date.now();
          setKeyboardViewportCommands(prev => ({
            ...prev,
            [viewportIndex]: { type: 'toggle_freeze', viewportIndex, studyInstanceUID, nonce },
          }));
          return;
        }
        setGlobalTool(shortcut.tool);
        return;
      }
      if (shortcut.type === 'windowLevel') {
        setWindowWidth(shortcut.wl.ww);
        setWindowCenter(shortcut.wl.wc);
        const nonce = Date.now();
        const commands = Array.from({ length: numViewports }).reduce((acc, _, viewportIndex) => {
          acc[viewportIndex] = { type: 'window_level', viewportIndex, wl: shortcut.wl, studyInstanceUID, nonce };
          return acc;
        }, {});
        setViewportWindowLevels(Array.from({ length: numViewports }).reduce((acc, _, viewportIndex) => {
          acc[viewportIndex] = shortcut.wl;
          return acc;
        }, {}));
        setKeyboardViewportCommands(commands);
      }
    };
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [numViewports, studyInstanceUID, keyboardToolShortcuts, activeReportViewportIndex, maximizedViewportIndex]);

  const handleKeyboardShortcutSave = async (prefs) => {
    const normalized = saveKeyboardShortcutPreferences(prefs);
    setKeyboardShortcutPrefs(normalized);
    setShortcutEditorOpen(false);
    try {
      await userPreferenceApi.save(KEYBOARD_SHORTCUT_PREF_NAMESPACE, normalized);
    } catch (e) {
      console.warn('Could not save keyboard shortcut preference; localStorage fallback preserved', e);
    }
  };

  const handleKeyboardShortcutReset = async () => {
    const normalized = saveKeyboardShortcutPreferences({});
    setKeyboardShortcutPrefs(normalized);
    try {
      await userPreferenceApi.save(KEYBOARD_SHORTCUT_PREF_NAMESPACE, normalized);
    } catch (e) {
      console.warn('Could not reset keyboard shortcut preference; localStorage fallback preserved', e);
    }
  };

  const handleToolLayoutSave = async (prefs) => {
    const normalized = saveToolLayoutPreferences(normalizeToolLayoutPreferences(prefs));
    setToolLayoutPrefs(normalized);
    setToolLayoutEditorOpen(false);
    try {
      await userPreferenceApi.save(TOOL_LAYOUT_PREF_NAMESPACE, normalized);
    } catch (e) {
      console.warn('Could not save tool layout preference; localStorage fallback preserved', e);
    }
  };

  const handleWorkspacePrefChange = React.useCallback(async (nextPrefs) => {
    const normalized = saveWorkspacePrefs(nextPrefs);
    setWorkspacePrefs(normalized);
    try {
      await userPreferenceApi.save(WORKSPACE_PREF_NAMESPACE, normalized);
    } catch (e) {
      console.warn('Could not save profile-backed workstation pixel focus preference; localStorage fallback preserved', e);
    }
  }, []);

  const handleWorkspacePresetChange = React.useCallback((presetId) => {
    handleWorkspacePrefChange(applyWorkspacePreset(presetId, workspacePrefs));
  }, [handleWorkspacePrefChange, workspacePrefs]);

  const handleMarkKeyObject = React.useCallback((payload) => {
    const mark = buildKeyObjectMark(payload, keyObjects.length);
    setKeyObjects(prev => [mark, ...prev.filter(item => item.id !== mark.id)].slice(0, 64));
  }, [keyObjects.length]);

  const resetToProtocol = () => {
    if (!hpLayout) return;
    const matched = LAYOUTS.find(l => l.rows === hpLayout.rows && l.cols === hpLayout.cols) || LAYOUTS[2];
    setManualLayout(null);
    applyViewportLayout({
      nextLayout: matched,
      nextPreloaded: assignments.map(a => a.series || null),
      nextSlotWLs: assignments.map(a => a.wl || { ww: 0, wc: 0 }),
      nextSlotLabels: assignments.map(a => a.label || ''),
    }, { force: true });
  };

  const handleOverlaySave = async (cfg) => {
    const normalized = normalizeOverlayConfig(cfg);
    saveOverlayConfig(normalized);
    setOverlayConfig(normalized);
    setOverlayDialogOpen(false);
    try {
      await userPreferenceApi.save(OVERLAY_PREF_NAMESPACE, normalized);
    } catch (e) {
      console.warn('Could not save overlay preference; localStorage fallback preserved', e);
    }
  };

  const handleHangingProtocolSave = async (protocols) => {
    const safeProtocols = Array.isArray(protocols) ? protocols : [];
    saveHangingProtocolOverrides(safeProtocols);
    setHangingProtocolOverrides(safeProtocols);
    setHpEditorOpen(false);
    try {
      await userPreferenceApi.save(HANGING_PROTOCOL_PREF_NAMESPACE, safeProtocols);
    } catch (e) {
      console.warn('Could not save hanging protocol preference; localStorage fallback preserved', e);
    }
  };

  const handleAnnotationAppearanceChange = React.useCallback(async (nextAppearance) => {
    const normalized = saveAnnotationAppearance(nextAppearance);
    setAnnotationAppearance(normalized);
    try {
      await userPreferenceApi.save(ANNOTATION_APPEARANCE_PREF_NAMESPACE, normalized);
    } catch (e) {
      console.warn('Could not save annotation appearance preference; localStorage fallback preserved', e);
    }
  }, []);


  const writeFhirWindow = (win, title, bodyHtml) => {
    win.document.open();
    win.document.write(`<html><head><title>${title}</title><style>body{background:#0a0a0a;color:#d7ffd7;font-family:monospace;white-space:pre-wrap;padding:16px} .error{color:#ff9f9f}</style></head><body>${bodyHtml}</body></html>`);
    win.document.close();
  };

  const openFhirJson = async (url, title) => {
    const win = window.open('', '_blank');
    if (!win) {
      alert('Could not open FHIR resource window. Please allow popups for this site.');
      return;
    }
    writeFhirWindow(win, title, 'Loading FHIR resource…');
    try {
      const res = await axios.get(url);
      const json = JSON.stringify(res.data, null, 2);
      const escapedJson = json.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
      writeFhirWindow(win, title, escapedJson);
    } catch (e) {
      console.warn('Could not open FHIR resource', e);
      const message = e?.response?.data?.detail || e.message || 'unknown error';
      writeFhirWindow(win, title, `<span class="error">Could not open FHIR resource: ${String(message).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))}</span>`);
    }
  };

  const mprBaseSeriesForStatus = React.useMemo(
    () => series.find(s => s.seriesInstanceUID === (activeSeriesId || series[0]?.seriesInstanceUID)),
    [series, activeSeriesId]
  );
  const mprPetSeriesForStatus = React.useMemo(
    () => series.find(s => s.seriesInstanceUID === fusionPetSeriesId),
    [series, fusionPetSeriesId]
  );
  const activeSeriesForStatus = React.useMemo(
    () => series.find(s => s.seriesInstanceUID === activeSeriesId) || mprBaseSeriesForStatus,
    [series, activeSeriesId, mprBaseSeriesForStatus]
  );
  const mprWindowLevel = React.useMemo(() => {
    const mprSeriesId = activeSeriesId || series[0]?.seriesInstanceUID;
    const mprSeries = series.find(s => s.seriesInstanceUID === mprSeriesId);
    const slotIndex = Array.isArray(preloaded) ? preloaded.findIndex(s => s?.seriesInstanceUID === mprSeriesId) : -1;
    const candidates = [
      slotIndex >= 0 ? viewportWindowLevels[slotIndex] : null,
      slotIndex >= 0 ? slotWLs[slotIndex] : null,
      { ww: windowWidth, wc: windowCenter },
    ];
    for (const wl of candidates) {
      const ww = Number(wl?.ww);
      const wc = Number(wl?.wc);
      if (Number.isFinite(ww) && ww > 0 && Number.isFinite(wc)) {
        return { ww, wc };
      }
    }
    // MPR's CT base series should be deterministic when no viewport/HP W/L has
    // initialized yet.  Falling back to auto (0/0) recreates the PET fusion color
    // mismatch this path fixes because CT auto-window changes the compositing base.
    if (modalityOf(mprSeries) === 'CT') {
      return WL.CT_SOFT;
    }
    return { ww: windowWidth, wc: windowCenter };
  }, [activeSeriesId, series, preloaded, viewportWindowLevels, slotWLs, windowWidth, windowCenter]);

  const commandDeckStatus = React.useMemo(() => {
    const preset = WORKSPACE_PRESETS.find(item => item.id === workspacePrefs.workspacePreset);
    const activeTool = TOOL_CLUSTERS.flatMap(cluster => cluster.tools).find(tool => tool.value === globalTool);
    return {
      presetLabel: preset?.label || 'CUSTOM',
      viewportLabel: `VP ${Math.max(0, activeReportViewportIndex || 0) + 1}/${numViewports}`,
      activeSeriesLabel: describeActiveSeriesForCommandDeck(activeSeriesForStatus),
      anatomyLabel: describeAnatomyForCommandDeck(activeSeriesForStatus),
      acquisitionTimeLabel: describeAcquisitionTimeForCommandDeck(activeSeriesForStatus),
      studyDateLabel: describeStudyDateForCommandDeck(studyInfo, activeSeriesForStatus),
      studyDescriptionLabel: describeStudyDescriptionForCommandDeck(studyInfo),
      studyTimeLabel: describeStudyTimeForCommandDeck(studyInfo, activeSeriesForStatus),
      modalityLabel: describeModalityForCommandDeck(studyInfo, activeSeriesForStatus),
      protocolNameLabel: describeProtocolNameForCommandDeck(activeSeriesForStatus),
      seriesNumberLabel: describeSeriesNumberForCommandDeck(activeSeriesForStatus),
      acquisitionNumberLabel: describeAcquisitionNumberForCommandDeck(activeSeriesForStatus),
      pixelSpacingLabel: describePixelSpacingForCommandDeck(activeSeriesForStatus),
      manufacturerLabel: describeManufacturerForCommandDeck(activeSeriesForStatus),
      scannerModelLabel: describeScannerModelForCommandDeck(activeSeriesForStatus),
      institutionLabel: describeInstitutionForCommandDeck(activeSeriesForStatus, studyInfo),
      stationLabel: describeStationForCommandDeck(activeSeriesForStatus),
      accessionLabel: describeAccessionForCommandDeck(studyInfo),
      patientIdLabel: describePatientIdForCommandDeck(studyInfo),
      patientDemographicsLabel: describePatientDemographicsForCommandDeck(studyInfo),
      contrastPhaseLabel: describeContrastPhaseForCommandDeck(activeSeriesForStatus),
      sequenceLabel: describeSequenceForCommandDeck(activeSeriesForStatus),
      reconKernelLabel: describeReconKernelForCommandDeck(activeSeriesForStatus),
      patientPositionLabel: describePatientPositionForCommandDeck(activeSeriesForStatus),
      toolLabel: activeTool?.label || String(globalTool || 'SCROLL').toUpperCase(),
      railLabel: `SERIES ${effectiveSeriesRailVisible ? 'ON' : 'OFF'} · REPORT ${effectiveReportRailVisible ? 'ON' : 'OFF'}`,
      flowLabel: `LINK ${linkedScroll ? 'ON' : 'OFF'} · REF ${showRefLines ? 'ON' : 'OFF'}`,
      protocolLabel: describeHangingProtocolForCommandDeck({ protocol, manualLayout }),
      compareLabel: comparisonContext?.label ? `COMPARE ${comparisonContext.label}` : 'PRIMARY READ',
      reportContextLabel: describeReportContextForCommandDeck(activeReportContext),
      sliceProgressLabel: describeViewportSliceProgress(viewportSliceProgress[activeReportViewportIndex || 0]),
      zoomFitLabel: describeViewportZoomFit(viewportZoomFit[activeReportViewportIndex || 0]),
      windowLevelLabel: describeViewportWindowLevel(viewportWindowLevels[activeReportViewportIndex || 0]),
      freezeLabel: describeViewportFreezeForCommandDeck(viewportFreezeState[activeReportViewportIndex || 0]),
      planeLabel: describeViewportPlaneForCommandDeck({ series: activeSeriesForStatus, mprMode }),
      sliceThicknessLabel: describeSliceThicknessForCommandDeck(activeSeriesForStatus),
      seriesCountLabel: describeSeriesCountForCommandDeck(activeSeriesForStatus),
      mprFusionLabel: describeMprFusionForCommandDeck({
        mprMode,
        baseSeries: mprBaseSeriesForStatus,
        petSeries: mprPetSeriesForStatus,
        fusionOpacity,
        fusionColormap,
        fusionPetSlabMm,
      }),
      shortcutLabel: viewerShortcutLabel,
    };
  }, [workspacePrefs.workspacePreset, globalTool, activeReportViewportIndex, numViewports, effectiveSeriesRailVisible, effectiveReportRailVisible, linkedScroll, showRefLines, protocol, manualLayout, comparisonContext, activeReportContext, viewportSliceProgress, viewportZoomFit, viewportWindowLevels, viewportFreezeState, activeSeriesForStatus, mprBaseSeriesForStatus, mprPetSeriesForStatus, fusionOpacity, fusionColormap, fusionPetSlabMm, mprMode, studyInfo]);

  return (
    <Box
      data-kairos-toolbar-density={toolLayoutPrefs.toolbarSize}
      style={{ '--kairos-toolbar-font-scale': getToolbarFontScale(toolLayoutPrefs) }}
      sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#0d0d0d' }}
    >
      {/* Toolbar */}
      <Box data-command-deck data-tool-layout-profile="viewer.tool_layout" data-tool-layout-top-count={topToolIds.size} data-command-deck-premium-rounded="KAIROS_COMMAND_DECK_PREMIUM_ROUNDED_CONTROLS" data-command-deck-density="KAIROS_COMMAND_DECK_DENSITY_COMPACT_ROWS" data-command-deck-collapsed={workspacePrefs.commandDeckCollapsed ? 'KAIROS_QOL_COMMAND_DECK_COLLAPSED' : undefined} sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--kairos-toolbar-deck-gap, 3.6px)', rowGap: 'var(--kairos-toolbar-deck-row-gap, 2.8px)',
        px: 'var(--kairos-toolbar-deck-px, 6px)', py: workspacePrefs.commandDeckCollapsed ? 'var(--kairos-toolbar-control-py, 2px)' : 'var(--kairos-toolbar-deck-py, 3.6px)',
        borderBottom: '1px solid rgba(110,239,255,0.14)',
        bgcolor: '#080b0f',
        background: 'linear-gradient(180deg, rgba(10,18,26,0.96), rgba(5,7,10,0.98))',
        minHeight: workspacePrefs.commandDeckCollapsed ? 30 : 46,
        '& [data-command-deck-group]': workspacePrefs.commandDeckCollapsed ? { display: 'none' } : undefined,
        '& [data-command-deck-group="WORKSTATION"]': workspacePrefs.commandDeckCollapsed ? { display: 'flex' } : undefined,
        '& .MuiToggleButton-root': { minHeight: 'var(--kairos-toolbar-control-min-height, 24px)', borderRadius: 1.25 },
        '& .MuiButton-root': { minHeight: 'var(--kairos-toolbar-control-min-height, 24px)', borderRadius: 1.25 },
        '& .MuiIconButton-root': { borderRadius: 1.25 },
      }}>
        <Tooltip title="Back to study list">
          <IconButton size="small" onClick={() => navigate('/')} sx={{ color: 'text.secondary' }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography sx={{ fontSize: 11, color: 'primary.main', letterSpacing: 2, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
          ◈ KAIROS
        </Typography>
        <Typography sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)', color: '#444', ml: 0.5, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
          {studyInstanceUID.slice(-20)}
        </Typography>
        <Box aria-hidden="true" data-command-deck-spacer="compact-xl" sx={{ flex: { xs: '1 1 auto', xl: '0 0 0px' }, width: { xs: 'auto', xl: 0 }, minWidth: 0 }} />
        {workspacePrefs.commandDeckCollapsed && (
          <Typography sx={{ fontSize: 'var(--kairos-toolbar-small-font-size, 9px)', color: '#6eefff', fontFamily: 'monospace', letterSpacing: 1, mr: 1 }}>TOOLBAR COMPACT · diagnostic pixels protected</Typography>
        )}
        {/* Logged-in user + logout */}
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1.5 }}>
            <Typography sx={{ fontSize: 'var(--kairos-toolbar-small-font-size, 9px)', color: '#445566', letterSpacing: 1, fontFamily: 'monospace' }}>
              {user.username}
            </Typography>
            <Tooltip title="My viewer profile settings">
              <IconButton
                size="small"
                aria-label="Open my viewer profile settings"
                onClick={() => navigate('/settings')}
                sx={{ color: '#55717a', p: 0.3, '&:hover': { color: 'primary.main', bgcolor: 'rgba(0,188,212,0.08)' } }}
              >
                <SettingsIcon sx={{ fontSize: 'var(--kairos-toolbar-icon-size, 13px)' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sign out">
              <IconButton
                size="small"
                onClick={handleLogout}
                sx={{ color: '#445566', p: 0.3, '&:hover': { color: '#ff6b6b', bgcolor: 'rgba(255,107,107,0.08)' } }}
              >
                <LogoutIcon sx={{ fontSize: 'var(--kairos-toolbar-icon-size, 13px)' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {/* Hanging protocol indicator + reset button */}
        {protocol && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
            <Tooltip title={`Hanging Protocol: ${protocol.name}`}>
              <Typography sx={{ fontSize: 'var(--kairos-toolbar-small-font-size, 9px)', color: manualLayout ? '#444' : '#335544', letterSpacing: 1, cursor: 'default', fontFamily: 'monospace' }}>
                ◈ {protocol.name.toUpperCase()}
              </Typography>
            </Tooltip>
            {manualLayout && (
              <Tooltip title={`Reset to protocol: ${protocol.name}`}>
                <IconButton
                  size="small"
                  onClick={resetToProtocol}
                  sx={{ color: '#335544', p: 0.3, '&:hover': { color: '#00bcd4', bgcolor: 'rgba(0,188,212,0.08)' } }}
                >
                  <RestartAltIcon sx={{ fontSize: 'var(--kairos-toolbar-icon-size, 13px)' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
        <Box sx={{ flexBasis: { xs: '100%', xl: 0 }, display: { xs: 'block', xl: 'none' }, height: 0 }} />
        <CommandDeckGroup label="LAYOUT / FLOW">
        <Typography sx={{ fontSize: 'var(--kairos-toolbar-small-font-size, 9px)', color: '#6f8794', mr: 0.25, whiteSpace: 'nowrap' }}>LAYOUT</Typography>
        <LayoutGridPicker layouts={LAYOUTS} activeLayout={activeLayout} onChange={handleLayoutChange} />
        {/* Linked scroll toggle */}
        <Tooltip title={linkedScroll ? 'Linked scroll ON' : 'Linked scroll OFF'}>
          <IconButton
            size="small"
            onClick={handleToggleLinkedScroll}
            aria-label={linkedScroll ? 'Disable linked scroll' : 'Enable linked scroll'}
            aria-pressed={linkedScroll}
            data-command-deck-icon-a11y="KAIROS_COMMAND_DECK_ICON_TOGGLE_A11Y"
            sx={{
              color: linkedScroll ? '#00bcd4' : '#555',
              bgcolor: linkedScroll ? 'rgba(0,188,212,0.15)' : 'transparent',
              '&:hover': { color: '#00bcd4', bgcolor: 'rgba(0,188,212,0.1)' },
            }}
          >
            <LinkIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={showRefLines ? 'Hide reference lines' : 'Show reference lines'}>
          <IconButton size="small" onClick={() => setShowRefLines(v => !v)}
            aria-label={showRefLines ? 'Hide reference lines' : 'Show reference lines'}
            aria-pressed={showRefLines}
            data-command-deck-icon-a11y="KAIROS_COMMAND_DECK_ICON_TOGGLE_A11Y"
            sx={{ color: showRefLines ? '#00bcd4' : '#555', bgcolor: showRefLines ? 'rgba(0,188,212,0.15)' : 'transparent' }}>
            <GridOnIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Choose Measure, ROI, Annotate, and right-click tools saved to my viewer profile">
          <Button
            size="small"
            variant="outlined"
            onClick={() => setToolLayoutEditorOpen(true)}
            aria-label="Edit my viewer toolbar and context menu tools"
            aria-haspopup="dialog"
            data-kairos-marker="KAIROS_VIEWER_PROFILE_TOOLS_TOOLBAR_ENTRY"
            sx={{ ml: 0.5, fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: '#ffe97a', borderColor: 'rgba(255,235,59,0.34)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}
          >
            MY TOOLS
          </Button>
        </Tooltip>
        <Tooltip title="Configure viewport overlays">
          <Button
            size="small"
            variant="outlined"
            onClick={() => setOverlayDialogOpen(true)}
            aria-label="Configure viewport overlays"
            data-kairos-marker="KAIROS_OVERLAY_NEXT_TO_MY_TOOLS"
            sx={{ ml: 0.5, fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: overlayConfig.enabled ? '#00bcd4' : '#666', borderColor: overlayConfig.enabled ? '#00bcd4' : '#2a2a2a' }}
          >
            OVERLAY
          </Button>
        </Tooltip>
        <Tooltip title="Open synced pop-out viewer windows">
          <Button
            size="small"
            variant="outlined"
            onClick={() => setMultiMonitorOpen(true)}
            aria-label="Open multi-monitor viewer windows"
            data-kairos-marker="KAIROS_MULTI_NEXT_TO_OVERLAY"
            startIcon={<OpenInNewIcon sx={{ fontSize: 'var(--kairos-toolbar-icon-size, 13px)' }} />}
            sx={{ ml: 0.5, fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: workspaceParams.monitorId !== 'primary' ? '#00bcd4' : '#666', borderColor: workspaceParams.monitorId !== 'primary' ? '#00bcd4' : '#2a2a2a' }}
          >
            MULTI
          </Button>
        </Tooltip>
        <Tooltip title="Edit the keyboard shortcuts saved to my viewer profile">
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShortcutEditorOpen(true)}
            aria-label="Edit my profile shortcuts"
            aria-haspopup="dialog"
            data-kairos-marker={KAIROS_VIEWER_PROFILE_SHORTCUTS_TOOLBAR_ENTRY}
            sx={{ ml: 0.5, fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: '#6eefff', borderColor: 'rgba(110,239,255,0.34)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}
          >
            MY SHORTCUTS
          </Button>
        </Tooltip>
        </CommandDeckGroup>
        {/* Global tool selector */}
        <CommandDeckGroup label="TOOLS">
        <Box aria-label="Viewport tool selection" role="group" data-tool-keycaps-profiled="KAIROS_TOOL_KEYCAPS_PROFILED" data-tool-completion-mode="KAIROS_TOOL_COMPLETION_MODE" sx={{ ml: { xs: 0, md: 0.5 }, display: 'flex', flexWrap: 'wrap', rowGap: 0.25, alignItems: 'stretch' }}>
          {TOOL_CLUSTERS.map(cluster => ({ ...cluster, tools: cluster.tools.filter(tool => topToolIds.has(tool.value)) })).filter(cluster => cluster.tools.length > 0).map(cluster => (
            <ToolCluster key={cluster.label} cluster={cluster} selectedTool={globalTool} onSelectTool={setGlobalTool} toolShortcuts={keyboardToolShortcuts} />
          ))}
        </Box>
        </CommandDeckGroup>
        {/* MPR toggle */}
        <CommandDeckGroup label="ADVANCED">
        <Tooltip title="Multi-Planar Reconstruction (axial / coronal / sagittal)">
          <Button
            size="small"
            variant={mprMode ? 'contained' : 'outlined'}
            onClick={() => {
              if (!mprMode) {
                const { baseSeries, petSeries } = pickDefaultMprFusionPair(series);
                if (baseSeries) setActiveSeriesId(baseSeries.seriesInstanceUID);
                if (petSeries) setFusionPetSeriesId(petSeries.seriesInstanceUID);
              }
              setMprMode(m => !m);
            }}
            startIcon={<ViewInArIcon sx={{ fontSize: 'var(--kairos-toolbar-icon-size, 13px)' }} />}
            sx={{
              ml: 0.5, fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)',
              minWidth: 0, borderRadius: 1.25,
              ...(mprMode
                ? { bgcolor: '#003344', color: 'primary.main', borderColor: '#00bcd4' }
                : { color: '#666', borderColor: '#2a2a2a' }
              ),
            }}
          >
            MPR
          </Button>
        </Tooltip>
        {/* VRT toggle */}
        <Tooltip title="3D Volume Rendering (bone / soft-tissue / MIP)">
          <Button
            size="small"
            variant={vrtSlot !== null ? 'contained' : 'outlined'}
            onClick={() => setVrtSlot(s => s !== null ? null : numViewports - 1)}
            startIcon={<ViewInArIcon sx={{ fontSize: 'var(--kairos-toolbar-icon-size, 13px)' }} />}
            sx={{
              ml: 0.5, fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)',
              minWidth: 0, borderRadius: 1.25,
              ...(vrtSlot !== null
                ? { bgcolor: '#003344', color: '#00bcd4', borderColor: '#00bcd4' }
                : { color: '#666', borderColor: '#2a2a2a' }
              ),
            }}
          >
            VRT
          </Button>
        </Tooltip>
        <Tooltip title="Open current study as FHIR ImagingStudy">
          <Button
            size="small"
            variant="outlined"
            onClick={() => openFhirJson(buildFhirImagingStudyUrl(studyInstanceUID), 'FHIR ImagingStudy')}
            startIcon={<HubIcon sx={{ fontSize: 'var(--kairos-toolbar-icon-size, 13px)' }} />}
            sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: '#66bb6a', borderColor: '#2a4a2a' }}
          >
            FHIR ImagingStudy
          </Button>
        </Tooltip>
        <Box data-command-deck-accessibility="KAIROS_DESKTOP_COMMAND_DECK_A11Y" aria-label="Edit hanging protocol preferences">
          <AdvancedOverflowMenu
            onOpenFhirPatient={() => openFhirJson(buildFhirPatientUrl(studyInfo?.patientId || ''), 'FHIR Patient')}
            patientDisabled={!studyInfo?.patientId}
            smartHref={buildSmartLaunchUrl({ patientId: studyInfo?.patientId || '', studyInstanceUID })}
            onOpenHangingProtocols={() => setHpEditorOpen(true)}
          />
        </Box>
        <Tooltip title="Create a study QC ticket for imaging operations">
          <Button
            size="small"
            variant="outlined"
            onClick={() => setQcTicketOpen(true)}
            disabled={!studyInstanceUID}
            aria-label="Create QC ticket for current study"
            data-kairos-marker={KAIROS_VIEWER_QC_TICKET}
            startIcon={<ReportProblemIcon sx={{ fontSize: 'var(--kairos-toolbar-icon-size, 13px)' }} />}
            sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: '#ffb74d', borderColor: '#4a321a' }}
          >
            QC
          </Button>
        </Tooltip>
        <Tooltip title={reportOpen ? "Hide native report" : "Show native report"}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setReportOpen(v => !v)}
            aria-label={reportOpen ? "Hide native report rail" : "Show native report rail"}
            aria-pressed={reportOpen}
            sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: reportOpen ? '#00bcd4' : '#666', borderColor: reportOpen ? '#00bcd4' : '#2a2a2a' }}
          >
            REPORT
          </Button>
        </Tooltip>
        <Tooltip title="Check this study open from the browser through accepted diagnostic pixels">
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setPerformanceReport(buildPerformanceReport());
              setPerformanceOpen(value => !value);
            }}
            aria-label="Toggle study performance check"
            aria-pressed={performanceOpen}
            data-kairos-performance-entry="ACCEPTED_PIXEL_BROWSER_DIAGNOSTICS"
            sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: performanceOpen ? '#6eefff' : '#78909c', borderColor: performanceOpen ? 'rgba(110,239,255,0.55)' : '#2a2a2a', fontFamily: 'monospace' }}
          >
            PERF
          </Button>
        </Tooltip>
        </CommandDeckGroup>
        <CommandDeckGroup label="WORKSTATION">
        <Box aria-label="Profile-backed workspace presets" role="group" data-testid="workspace-preset-deck" sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 0.35, py: 0.15, borderLeft: '1px solid rgba(110,239,255,0.12)' }}>
          <Typography sx={{ fontSize: 'var(--kairos-toolbar-cluster-label-font-size, 7px)', color: 'rgba(215,251,255,0.38)', letterSpacing: 1.1, fontFamily: 'monospace', fontWeight: 800, mr: 0.2 }}>PRESET</Typography>
          {WORKSPACE_PRESETS.map(preset => (
            <Tooltip key={preset.id} title={preset.description}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleWorkspacePresetChange(preset.id)}
                aria-label={`Apply ${preset.label} workspace preset`}
                aria-pressed={workspacePrefs.workspacePreset === preset.id}
                data-workspace-preset={preset.id}
                sx={{ fontSize: 'var(--kairos-toolbar-small-font-size, 9px)', py: 0.2, px: 0.55, minWidth: 0, borderRadius: 1.25, fontFamily: 'monospace', color: workspacePrefs.workspacePreset === preset.id ? '#0b1220' : '#8aa8b8', bgcolor: workspacePrefs.workspacePreset === preset.id ? 'rgba(110,239,255,0.82)' : 'transparent', borderColor: workspacePrefs.workspacePreset === preset.id ? 'rgba(110,239,255,0.95)' : '#2a2a2a', '&:hover': { borderColor: 'rgba(110,239,255,0.75)', bgcolor: workspacePrefs.workspacePreset === preset.id ? 'rgba(110,239,255,0.92)' : 'rgba(110,239,255,0.08)' } }}
              >
                {preset.label}
              </Button>
            </Tooltip>
          ))}
        </Box>
        <Tooltip title={workspacePrefs.commandDeckCollapsed ? "Dock full toolbar" : "Collapse toolbar into compact dock"}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleWorkspacePrefChange({ ...workspacePrefs, workspacePreset: WORKSPACE_PRESET_IDS.CUSTOM, commandDeckCollapsed: !workspacePrefs.commandDeckCollapsed })}
            aria-label={workspacePrefs.commandDeckCollapsed ? "Dock full toolbar" : "Collapse toolbar"}
            aria-pressed={workspacePrefs.commandDeckCollapsed}
            sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: workspacePrefs.commandDeckCollapsed ? '#b6ff7a' : '#667788', borderColor: workspacePrefs.commandDeckCollapsed ? 'rgba(182,255,122,0.48)' : '#2a2a2a', fontFamily: 'monospace' }}
          >
            TOOLBAR {workspacePrefs.commandDeckCollapsed ? 'DOCK' : 'COLLAPSE'}
          </Button>
        </Tooltip>
        <Tooltip title={workspacePrefs.toolCompletionBehavior === TOOL_COMPLETION_BEHAVIORS.STAY ? "Tools stay active for repeated measurements/annotations" : "Tools return to scroll after each completed measurement/annotation"}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleWorkspacePrefChange({ ...workspacePrefs, workspacePreset: WORKSPACE_PRESET_IDS.CUSTOM, toolCompletionBehavior: workspacePrefs.toolCompletionBehavior === TOOL_COMPLETION_BEHAVIORS.STAY ? TOOL_COMPLETION_BEHAVIORS.SCROLL : TOOL_COMPLETION_BEHAVIORS.STAY })}
            aria-label="Toggle tool completion behavior"
            aria-pressed={workspacePrefs.toolCompletionBehavior === TOOL_COMPLETION_BEHAVIORS.STAY}
            data-tool-completion-mode="KAIROS_TOOL_COMPLETION_MODE"
            sx={{ ml: 0.5, fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: workspacePrefs.toolCompletionBehavior === TOOL_COMPLETION_BEHAVIORS.STAY ? '#b6ff7a' : '#667788', borderColor: workspacePrefs.toolCompletionBehavior === TOOL_COMPLETION_BEHAVIORS.STAY ? 'rgba(182,255,122,0.48)' : '#2a2a2a', fontFamily: 'monospace' }}
          >
            TOOLS {workspacePrefs.toolCompletionBehavior === TOOL_COMPLETION_BEHAVIORS.STAY ? 'STAY' : 'SCROLL'}
          </Button>
        </Tooltip>
        <Tooltip title={workspacePrefs.escapeReturnsToScroll ? "Escape returns tools to scroll/default" : "Escape clears transient state only"}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleWorkspacePrefChange({ ...workspacePrefs, workspacePreset: WORKSPACE_PRESET_IDS.CUSTOM, escapeReturnsToScroll: !workspacePrefs.escapeReturnsToScroll })}
            aria-label="Toggle Escape returns to scroll preference"
            aria-pressed={workspacePrefs.escapeReturnsToScroll}
            sx={{ ml: 0.5, fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: workspacePrefs.escapeReturnsToScroll ? '#6eefff' : '#667788', borderColor: workspacePrefs.escapeReturnsToScroll ? 'rgba(110,239,255,0.34)' : '#2a2a2a', fontFamily: 'monospace' }}
          >
            ESC {workspacePrefs.escapeReturnsToScroll ? 'SCROLL' : 'CLEAR'}
          </Button>
        </Tooltip>
        <Tooltip title={workspacePrefs.pixelFocus ? "Exit profile-backed workstation pixel focus" : "Profile-backed workstation pixel focus hides side rails without overlaying diagnostic pixels"}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleWorkspacePrefChange({ ...workspacePrefs, workspacePreset: WORKSPACE_PRESET_IDS.CUSTOM, pixelFocus: !workspacePrefs.pixelFocus })}
            aria-label="Toggle workstation pixel focus"
            aria-pressed={workspacePrefs.pixelFocus}
            sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: workspacePrefs.pixelFocus ? '#b6ff7a' : '#667788', borderColor: workspacePrefs.pixelFocus ? 'rgba(182,255,122,0.48)' : '#2a2a2a', fontFamily: 'monospace' }}
          >
            PIXEL FOCUS
          </Button>
        </Tooltip>
        <Tooltip title={workspacePrefs.seriesRailVisible ? "Hide profile-backed Series rail without covering diagnostic pixels" : "Restore profile-backed Series rail exactly as saved"}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleWorkspacePrefChange({ ...workspacePrefs, workspacePreset: WORKSPACE_PRESET_IDS.CUSTOM, seriesRailVisible: !workspacePrefs.seriesRailVisible })}
            aria-label="Toggle series rail"
            aria-pressed={workspacePrefs.seriesRailVisible}
            sx={{ ml: 0.5, fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: workspacePrefs.seriesRailVisible ? '#6eefff' : '#667788', borderColor: workspacePrefs.seriesRailVisible ? 'rgba(110,239,255,0.34)' : '#2a2a2a', fontFamily: 'monospace' }}
          >
            SERIES RAIL {workspacePrefs.seriesRailVisible ? 'ON' : 'OFF'}
          </Button>
        </Tooltip>
        <Tooltip title={workspacePrefs.reportRailVisible ? "Hide profile-backed Report rail while keeping report state mounted safely" : "Restore profile-backed Report rail exactly as saved"}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleWorkspacePrefChange({ ...workspacePrefs, workspacePreset: WORKSPACE_PRESET_IDS.CUSTOM, reportRailVisible: !workspacePrefs.reportRailVisible })}
            aria-label="Toggle report rail"
            aria-pressed={workspacePrefs.reportRailVisible}
            sx={{ ml: 0.5, fontSize: 'var(--kairos-toolbar-font-size, 10px)', py: 'var(--kairos-toolbar-control-py, 2px)', px: 'var(--kairos-toolbar-control-px, 6px)', minWidth: 0, borderRadius: 1.25, color: workspacePrefs.reportRailVisible ? '#ffd166' : '#667788', borderColor: workspacePrefs.reportRailVisible ? 'rgba(255,209,102,0.38)' : '#2a2a2a', fontFamily: 'monospace' }}
          >
            REPORT RAIL {workspacePrefs.reportRailVisible ? 'ON' : 'OFF'}
          </Button>
        </Tooltip>
        {/* Help button */}
        <Tooltip title="Keyboard shortcuts (?)">
          <IconButton
            size="small"
            onClick={() => setShowHelp(v => !v)}
            aria-label={showHelp ? "Hide keyboard shortcuts help" : "Show keyboard shortcuts help"}
            aria-pressed={showHelp}
            sx={{
              color: showHelp ? "#00bcd4" : "#555",
              bgcolor: showHelp ? "rgba(0,188,212,0.15)" : "transparent",
              "&:hover": { color: "#00bcd4", bgcolor: "rgba(0,188,212,0.1)" },
            }}
          >
            <HelpOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        </CommandDeckGroup>
        {/* MPR series selector — only visible in MPR mode */}
        {mprMode && series.length > 0 && (
          <>
          <Box role="group" aria-label="MPR and PET fusion controls" data-mpr-fusion-controls="KAIROS_MPR_FUSION_COMMAND_DECK_A11Y" sx={{ display: 'flex', alignItems: 'center', ml: 0.75 }}>
          <FormControl size="small" sx={{ minWidth: 132 }}>
            <Select
              inputProps={{ 'aria-label': 'Select MPR base series' }}
              value={activeSeriesId || ''}
              onChange={e => {
                const nextSeriesId = e.target.value;
                setActiveSeriesId(nextSeriesId);
                const nextBase = series.find(s => s.seriesInstanceUID === nextSeriesId);
                const nextPet = pickBestPetForMprBase(series, nextBase);
                setFusionPetSeriesId(nextPet?.seriesInstanceUID || '');
              }}
              displayEmpty
              sx={{
                fontSize: 'var(--kairos-toolbar-font-size, 10px)', color: '#00bcd4', borderColor: '#00bcd4', borderRadius: 1.25,
                height: 'var(--kairos-toolbar-control-min-height, 24px)',
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#00bcd4' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00bcd4' },
                '.MuiSelect-icon': { color: '#00bcd4', fontSize: 14 },
                '.MuiSelect-select': { py: 0.3, px: 1 },
              }}
              MenuProps={{ PaperProps: { sx: { bgcolor: '#111', border: '1px solid #2a2a2a' } } }}
            >
              {series.filter(s => !['PT', 'PET'].includes(modalityOf(s))).map(s => (
                <MenuItem
                  key={s.seriesInstanceUID}
                  value={s.seriesInstanceUID}
                  sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)', color: '#ccc', py: 0.5, '&:hover': { bgcolor: '#1a2a30' }, '&.Mui-selected': { bgcolor: '#003344' } }}
                >
                  <span style={{ color: '#00bcd4', marginRight: 6, fontSize: 'var(--kairos-toolbar-small-font-size, 9px)' }}>{s.modality}</span>
                  {s.seriesDescription || `Series ${s.seriesNumber}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {series.some(s => ['PT', 'PET'].includes(modalityOf(s)) && (s.numInstances || s.num_instances || 0) >= 8) && (
            <>
              <FormControl size="small" sx={{ ml: 0.5, minWidth: 126 }}>
                <Select
                  inputProps={{ 'aria-label': 'Select PET fusion series' }}
                  value={fusionPetSeriesId || ''}
                  onChange={e => setFusionPetSeriesId(e.target.value)}
                  displayEmpty
                  sx={{
                    fontSize: 'var(--kairos-toolbar-font-size, 10px)', color: fusionPetSeriesId ? '#ff9800' : '#666', borderRadius: 1.25, height: 'var(--kairos-toolbar-control-min-height, 24px)',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: fusionPetSeriesId ? '#ff9800' : '#333' },
                    '.MuiSelect-icon': { color: fusionPetSeriesId ? '#ff9800' : '#666', fontSize: 14 },
                    '.MuiSelect-select': { py: 0.3, px: 1 },
                  }}
                  MenuProps={{ PaperProps: { sx: { bgcolor: '#111', border: '1px solid #2a2a2a' } } }}
                >
                  <MenuItem value="" sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)', color: '#777' }}>PET fusion off</MenuItem>
                  {eligiblePetFusionSeries(series).map(s => (
                    <MenuItem key={s.seriesInstanceUID} value={s.seriesInstanceUID} sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)', color: '#ccc', py: 0.5 }}>
                      <span style={{ color: '#ff9800', marginRight: 6, fontSize: 'var(--kairos-toolbar-small-font-size, 9px)' }}>PT</span>
                      {s.seriesDescription || `Series ${s.seriesNumber}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {fusionPetSeriesId && (
                <>
                  <Typography sx={{ fontSize: 'var(--kairos-toolbar-small-font-size, 9px)', color: '#ff9800', ml: 1 }}>PET</Typography>
                  <Slider aria-label="PET fusion opacity" value={fusionOpacity} min={0} max={1} step={0.05} onChange={(_, v) => setFusionOpacity(v)} size="small" sx={{ width: { xs: 56, md: 70 }, color: '#ff9800', mx: 0.5 }} />
                  <FormControl size="small" sx={{ minWidth: 82 }}>
                    <Select inputProps={{ 'aria-label': 'Select PET fusion colormap' }} value={fusionColormap} onChange={e => setFusionColormap(e.target.value)} sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)', color: '#ff9800', borderRadius: 1.25, height: 'var(--kairos-toolbar-control-min-height, 24px)', '.MuiOutlinedInput-notchedOutline': { borderColor: '#ff9800' }, '.MuiSelect-icon': { color: '#ff9800', fontSize: 14 }, '.MuiSelect-select': { py: 0.3, px: 1 } }}>
                      <MenuItem value="hot" sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)' }}>hot</MenuItem>
                      <MenuItem value="rainbow" sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)' }}>rainbow</MenuItem>
                      <MenuItem value="hot-body" sx={{ fontSize: 'var(--kairos-toolbar-font-size, 10px)' }}>hot-body</MenuItem>
                    </Select>
                  </FormControl>
                  <Box data-kairos-marker={PET_SLICE_THICKNESS_MARKER} sx={{ display: 'flex', alignItems: 'center', gap: 0.35, ml: 0.6, px: 0.45, py: 0.1, border: '1px solid rgba(255,204,128,0.36)', borderRadius: 1, bgcolor: 'rgba(16,10,2,0.55)' }}>
                    <Typography component="label" htmlFor="mpr-pet-avg-slab" sx={{ color: '#ffcc80', fontSize: 'var(--kairos-toolbar-label-font-size, 8px)', fontFamily: 'monospace', fontWeight: 900, letterSpacing: 0.7, whiteSpace: 'nowrap' }}>PT AVG SLAB</Typography>
                    <Tooltip title={PET_SLICE_THICKNESS_HELP_TEXT} placement="bottom" arrow>
                      <Typography component="button" type="button" data-kairos-marker={PET_SLICE_THICKNESS_HELP_MARKER} aria-label={PET_SLICE_THICKNESS_HELP_TEXT} sx={{ appearance: 'none', p: 0, m: 0, width: 13, height: 13, lineHeight: '13px', borderRadius: '50%', textAlign: 'center', color: '#2a1500', bgcolor: 'rgba(255,204,128,0.92)', border: '1px solid rgba(255,236,179,0.92)', fontSize: 'var(--kairos-toolbar-small-font-size, 9px)', fontFamily: 'monospace', fontWeight: 900, cursor: 'help', userSelect: 'none', '&:focus-visible': { outline: '1px solid #fff3c4', outlineOffset: 1 } }}>?</Typography>
                    </Tooltip>
                    <Box component="select" id="mpr-pet-avg-slab" aria-label="PET fusion average-slab display thickness" value={fusionPetSlabMm} onChange={e => setFusionPetSlabMm(Number(e.target.value) || 1)} sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: '#ffe0b2', border: '1px solid rgba(255,204,128,0.42)', borderRadius: '3px', fontSize: 'var(--kairos-toolbar-small-font-size, 9px)', fontFamily: 'monospace', height: 18, outline: 'none' }}>
                      {PET_SLICE_THICKNESS_OPTIONS_MM.map(mm => <option key={mm} value={mm}>{mm} mm</option>)}
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}
          </Box>
          </>
        )}
        {(
          <Box
            data-command-deck-status={COMMAND_DECK_STATUS_MARKER}
            data-command-deck-status-overflow={COMMAND_DECK_STATUS_OVERFLOW_MARKER}
            data-command-deck-clinical-status={COMMAND_DECK_CLINICAL_STATUS_MARKER}
            data-command-deck-presentation={COMMAND_DECK_PRESENTATION_MARKER}
            aria-label={`Command deck status: workspace ${commandDeckStatus.presetLabel}; active viewport ${commandDeckStatus.viewportLabel}; active series ${commandDeckStatus.activeSeriesLabel}; accession ${commandDeckStatus.accessionLabel}; patient id ${commandDeckStatus.patientIdLabel}; demographics ${commandDeckStatus.patientDemographicsLabel}; study ${commandDeckStatus.studyDescriptionLabel}; study date ${commandDeckStatus.studyDateLabel}; study time ${commandDeckStatus.studyTimeLabel}; modality ${commandDeckStatus.modalityLabel}; imaging protocol ${commandDeckStatus.protocolNameLabel}; series number ${commandDeckStatus.seriesNumberLabel}; series anatomy ${commandDeckStatus.anatomyLabel}; acquisition time ${commandDeckStatus.acquisitionTimeLabel}; contrast phase ${commandDeckStatus.contrastPhaseLabel}; sequence ${commandDeckStatus.sequenceLabel}; reconstruction kernel ${commandDeckStatus.reconKernelLabel}; patient position ${commandDeckStatus.patientPositionLabel}; tool ${commandDeckStatus.toolLabel}; ${commandDeckStatus.railLabel}; flow ${commandDeckStatus.flowLabel}; hanging protocol ${commandDeckStatus.protocolLabel}; priors ${commandDeckStatus.compareLabel}; report context ${commandDeckStatus.reportContextLabel}; active slice ${commandDeckStatus.sliceProgressLabel}; zoom ${commandDeckStatus.zoomFitLabel}; window level ${commandDeckStatus.windowLevelLabel}; viewport lock ${commandDeckStatus.freezeLabel}; viewport plane ${commandDeckStatus.planeLabel}; slice thickness ${commandDeckStatus.sliceThicknessLabel}; series acquisition ${commandDeckStatus.acquisitionNumberLabel}; pixel spacing ${commandDeckStatus.pixelSpacingLabel}; manufacturer ${commandDeckStatus.manufacturerLabel}; scanner model ${commandDeckStatus.scannerModelLabel}; institution ${commandDeckStatus.institutionLabel}; station ${commandDeckStatus.stationLabel}; series image count ${commandDeckStatus.seriesCountLabel}; MPR fusion ${commandDeckStatus.mprFusionLabel}; shortcuts ${commandDeckStatus.shortcutLabel}`}
            sx={{
              flexBasis: 'calc(100% - 8px)',
              ml: 0.25,
              mr: 0.25,
              display: { xs: 'none', xl: 'flex' },
              alignItems: 'center',
              gap: 0.5,
              px: 'var(--kairos-toolbar-control-px, 6px)',
              py: 'var(--kairos-toolbar-control-py, 2px)',
              border: '1px solid rgba(110,239,255,0.12)',
              borderRadius: 1.25,
              background: 'linear-gradient(135deg, rgba(4,12,18,0.92), rgba(8,18,26,0.74))',
              color: 'rgba(215,251,255,0.56)',
              fontFamily: 'monospace',
              fontSize: 'calc(8.5px * var(--kairos-toolbar-font-scale, 1))',
              letterSpacing: 0.8,
              whiteSpace: 'nowrap',
              maxWidth: 'none',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'thin',
              overscrollBehaviorX: 'contain',
              '& [data-command-status-empty="true"]': { display: 'none' },
              '& span:has(+ [data-command-status-empty="true"])': { display: 'none' },
              '& [data-testid]': { color: '#c6f6ff', fontWeight: 700 },
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(110,239,255,0.24)', borderRadius: 999 },
              '@media (max-width: 1500px)': { display: 'none' },
            }}
          >
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.78)', fontWeight: 900 }}>STATUS</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.presetLabel) ? 'true' : undefined} aria-hidden="true">{commandDeckStatus.presetLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.viewportLabel) ? 'true' : undefined} aria-hidden="true">{commandDeckStatus.viewportLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.activeSeriesLabel) ? 'true' : undefined} aria-hidden="true" data-testid="active-series-indicator" data-command-deck-active-series={COMMAND_DECK_ACTIVE_SERIES_MARKER} sx={{ color: commandDeckStatus.activeSeriesLabel === 'SERIES --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.activeSeriesLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.accessionLabel) ? 'true' : undefined} aria-hidden="true" data-testid="accession-indicator" data-command-deck-accession={COMMAND_DECK_ACCESSION_MARKER} sx={{ color: commandDeckStatus.accessionLabel === 'ACC --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.accessionLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.patientIdLabel) ? 'true' : undefined} aria-hidden="true" data-testid="patient-id-indicator" data-command-deck-patient-id={COMMAND_DECK_PATIENT_ID_MARKER} sx={{ color: commandDeckStatus.patientIdLabel === 'PID --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.patientIdLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.patientDemographicsLabel) ? 'true' : undefined} aria-hidden="true" data-testid="patient-demographics-indicator" data-command-deck-patient-demographics={COMMAND_DECK_PATIENT_DEMOGRAPHICS_MARKER} sx={{ color: commandDeckStatus.patientDemographicsLabel === 'DEM --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.patientDemographicsLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.studyDescriptionLabel) ? 'true' : undefined} aria-hidden="true" data-testid="study-description-indicator" data-command-deck-study-description={COMMAND_DECK_STUDY_DESCRIPTION_MARKER} sx={{ color: commandDeckStatus.studyDescriptionLabel === 'STUDY --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.studyDescriptionLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.anatomyLabel) ? 'true' : undefined} aria-hidden="true" data-testid="anatomy-indicator" data-command-deck-anatomy={COMMAND_DECK_ANATOMY_MARKER} sx={{ color: commandDeckStatus.anatomyLabel === 'SERIES ANAT --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.anatomyLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.studyDateLabel) ? 'true' : undefined} aria-hidden="true" data-testid="study-date-indicator" data-command-deck-study-date={COMMAND_DECK_STUDY_DATE_MARKER} sx={{ color: commandDeckStatus.studyDateLabel === 'DATE --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.studyDateLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.studyTimeLabel) ? 'true' : undefined} aria-hidden="true" data-testid="study-time-indicator" data-command-deck-study-time={COMMAND_DECK_STUDY_TIME_MARKER} sx={{ color: commandDeckStatus.studyTimeLabel === 'TIME --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.studyTimeLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.modalityLabel) ? 'true' : undefined} aria-hidden="true" data-testid="modality-indicator" data-command-deck-modality={COMMAND_DECK_MODALITY_MARKER} sx={{ color: commandDeckStatus.modalityLabel === 'MOD --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.modalityLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.protocolNameLabel) ? 'true' : undefined} aria-hidden="true" data-testid="protocol-name-indicator" data-command-deck-protocol-name={COMMAND_DECK_PROTOCOL_NAME_MARKER} sx={{ color: commandDeckStatus.protocolNameLabel === 'PROT --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.protocolNameLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.seriesNumberLabel) ? 'true' : undefined} aria-hidden="true" data-testid="series-number-indicator" data-command-deck-series-number={COMMAND_DECK_SERIES_NUMBER_MARKER} sx={{ color: commandDeckStatus.seriesNumberLabel === 'S# --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.seriesNumberLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.acquisitionNumberLabel) ? 'true' : undefined} aria-hidden="true" data-testid="acquisition-number-indicator" data-command-deck-acquisition-number={COMMAND_DECK_ACQUISITION_NUMBER_MARKER} sx={{ color: commandDeckStatus.acquisitionNumberLabel === 'ACQ# --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.acquisitionNumberLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.acquisitionTimeLabel) ? 'true' : undefined} aria-hidden="true" data-testid="acquisition-time-indicator" data-command-deck-acquisition-time={COMMAND_DECK_ACQUISITION_TIME_MARKER} sx={{ color: commandDeckStatus.acquisitionTimeLabel === 'ACQ --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.acquisitionTimeLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.contrastPhaseLabel) ? 'true' : undefined} aria-hidden="true" data-testid="contrast-phase-indicator" data-command-deck-contrast-phase={COMMAND_DECK_CONTRAST_PHASE_MARKER} sx={{ color: commandDeckStatus.contrastPhaseLabel === 'PHASE --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.contrastPhaseLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.sequenceLabel) ? 'true' : undefined} aria-hidden="true" data-testid="sequence-indicator" data-command-deck-sequence={COMMAND_DECK_SEQUENCE_MARKER} sx={{ color: commandDeckStatus.sequenceLabel === 'SEQ --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.sequenceLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.reconKernelLabel) ? 'true' : undefined} aria-hidden="true" data-testid="recon-kernel-indicator" data-command-deck-recon-kernel={COMMAND_DECK_RECON_KERNEL_MARKER} sx={{ color: commandDeckStatus.reconKernelLabel === 'KERNEL --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.reconKernelLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.patientPositionLabel) ? 'true' : undefined} aria-hidden="true" data-testid="patient-position-indicator" data-command-deck-patient-position={COMMAND_DECK_PATIENT_POSITION_MARKER} sx={{ color: commandDeckStatus.patientPositionLabel === 'POS --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.patientPositionLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.pixelSpacingLabel) ? 'true' : undefined} aria-hidden="true" data-testid="pixel-spacing-indicator" data-command-deck-pixel-spacing={COMMAND_DECK_PIXEL_SPACING_MARKER} sx={{ color: commandDeckStatus.pixelSpacingLabel === 'PX --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.pixelSpacingLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.manufacturerLabel) ? 'true' : undefined} aria-hidden="true" data-testid="manufacturer-indicator" data-command-deck-manufacturer={COMMAND_DECK_MANUFACTURER_MARKER} sx={{ color: commandDeckStatus.manufacturerLabel === 'MFR --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.manufacturerLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.scannerModelLabel) ? 'true' : undefined} aria-hidden="true" data-testid="scanner-model-indicator" data-command-deck-scanner-model={COMMAND_DECK_MODEL_MARKER} sx={{ color: commandDeckStatus.scannerModelLabel === 'MODEL --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.scannerModelLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.institutionLabel) ? 'true' : undefined} aria-hidden="true" data-testid="institution-indicator" data-command-deck-institution={COMMAND_DECK_INSTITUTION_MARKER} sx={{ color: commandDeckStatus.institutionLabel === 'SITE --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.institutionLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.stationLabel) ? 'true' : undefined} aria-hidden="true" data-testid="station-indicator" data-command-deck-station={COMMAND_DECK_STATION_MARKER} sx={{ color: commandDeckStatus.stationLabel === 'STN --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.stationLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.toolLabel) ? 'true' : undefined} aria-hidden="true">{commandDeckStatus.toolLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.railLabel) ? 'true' : undefined} aria-hidden="true">{commandDeckStatus.railLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.flowLabel) ? 'true' : undefined} aria-hidden="true">{commandDeckStatus.flowLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.protocolLabel) ? 'true' : undefined} aria-hidden="true" data-command-deck-protocol={COMMAND_DECK_PROTOCOL_STATUS_MARKER} sx={{ color: commandDeckStatus.protocolLabel.startsWith('HP CUSTOM') ? '#ffd166' : 'rgba(215,251,255,0.56)' }}>{commandDeckStatus.protocolLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.compareLabel) ? 'true' : undefined} aria-hidden="true" sx={{ color: commandDeckStatus.compareLabel === 'PRIMARY READ' ? 'rgba(215,251,255,0.56)' : '#ffd166' }}>{commandDeckStatus.compareLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.reportContextLabel) ? 'true' : undefined} aria-hidden="true" data-command-deck-report-context={COMMAND_DECK_REPORT_CONTEXT_MARKER} sx={{ color: commandDeckStatus.reportContextLabel === 'REPORT READY' ? 'rgba(215,251,255,0.56)' : '#9ad7ff' }}>{commandDeckStatus.reportContextLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.sliceProgressLabel) ? 'true' : undefined} aria-hidden="true" data-command-deck-slice-progress={COMMAND_DECK_SLICE_PROGRESS_MARKER} sx={{ color: commandDeckStatus.sliceProgressLabel === 'SLICE --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.sliceProgressLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.zoomFitLabel) ? 'true' : undefined} aria-hidden="true" data-testid="zoom-fit-indicator" data-command-deck-zoom-fit={COMMAND_DECK_ZOOM_FIT_MARKER} sx={{ color: commandDeckStatus.zoomFitLabel === 'ZOOM --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.zoomFitLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.windowLevelLabel) ? 'true' : undefined} aria-hidden="true" data-testid="window-level-indicator" data-command-deck-window-level={COMMAND_DECK_WINDOW_LEVEL_MARKER} data-command-deck-window-level-preset={COMMAND_DECK_WINDOW_LEVEL_PRESET_MARKER} sx={{ color: commandDeckStatus.windowLevelLabel === 'W/L AUTO' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.windowLevelLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.freezeLabel) ? 'true' : undefined} aria-hidden="true" data-testid="freeze-indicator" data-command-deck-freeze={COMMAND_DECK_FREEZE_MARKER} sx={{ color: commandDeckStatus.freezeLabel === 'LOCKED' ? '#b6ff7a' : 'rgba(215,251,255,0.48)' }}>{commandDeckStatus.freezeLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.planeLabel) ? 'true' : undefined} aria-hidden="true" data-testid="plane-indicator" data-command-deck-plane={COMMAND_DECK_PLANE_MARKER} sx={{ color: commandDeckStatus.planeLabel === 'PLANE --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.planeLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.sliceThicknessLabel) ? 'true' : undefined} aria-hidden="true" data-testid="slice-thickness-indicator" data-command-deck-slice-thickness={COMMAND_DECK_SLICE_THICKNESS_MARKER} sx={{ color: commandDeckStatus.sliceThicknessLabel === 'THICK --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.sliceThicknessLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.seriesCountLabel) ? 'true' : undefined} aria-hidden="true" data-testid="series-count-indicator" data-command-deck-series-count={COMMAND_DECK_SERIES_COUNT_MARKER} sx={{ color: commandDeckStatus.seriesCountLabel === 'IMAGES --' ? 'rgba(215,251,255,0.48)' : '#c6f6ff' }}>{commandDeckStatus.seriesCountLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.mprFusionLabel) ? 'true' : undefined} aria-hidden="true" data-testid="mpr-fusion-indicator" data-command-deck-mpr-fusion={COMMAND_DECK_MPR_FUSION_MARKER} sx={{ color: commandDeckStatus.mprFusionLabel === 'MPR OFF' ? 'rgba(215,251,255,0.48)' : '#ffcc80' }}>{commandDeckStatus.mprFusionLabel}</Box>
            <Box component="span" aria-hidden="true" sx={{ color: 'rgba(110,239,255,0.32)' }}>•</Box>
            <Box component="span" data-command-status-empty={isCommandDeckPlaceholder(commandDeckStatus.shortcutLabel) ? 'true' : undefined} aria-hidden="true">{`? ${commandDeckStatus.shortcutLabel}`}</Box>
          </Box>
        )}      </Box>
      {performanceOpen && (
        <StudyPerformanceDiagnosticStrip
          report={performanceReport || buildPerformanceReport()}
          traceId={performanceEventId}
          submitState={performanceSubmitState}
          onSubmit={() => finalizePerformanceSession(performanceAcceptedViewportsRef.current.size ? 'partial' : 'timeout')}
          onClose={() => setPerformanceOpen(false)}
        />
      )}
      {compareRouteNotice && (
        <Box sx={{ px: 1.25, py: 0.45, borderBottom: '1px solid #2a2610', bgcolor: 'rgba(255,235,59,0.08)' }}>
          <Typography sx={{ color: '#fff59d', fontSize: 'var(--kairos-toolbar-font-size, 10px)', fontFamily: 'monospace' }}>{compareRouteNotice}</Typography>
        </Box>
      )}

      {/* Main content */}
      <Box key={`study-content-${studyInstanceUID}`} sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', overflow: 'hidden' }}>
        <Box
          data-series-rail-position={workspacePrefs.seriesRailPosition}
          sx={{
            flex: 1, minHeight: 0, minWidth: 0, display: 'flex', overflow: 'hidden',
            flexDirection: workspacePrefs.seriesRailPosition === 'top' ? 'column' : workspacePrefs.seriesRailPosition === 'bottom' ? 'column-reverse' : 'row',
          }}
        >
        <Box sx={{
              width: effectiveSeriesRailVisible ? (workspacePrefs.seriesRailPosition === 'left' ? 180 : '100%') : 0,
              height: effectiveSeriesRailVisible ? (workspacePrefs.seriesRailPosition === 'left' ? '100%' : 176) : 0,
              flexShrink: 0, overflow: 'hidden', position: 'relative',
              display: effectiveSeriesRailVisible ? 'flex' : 'none', flexDirection: 'column',
            }}>
          <SeriesPanel
            orientation={workspacePrefs.seriesRailPosition === 'left' ? 'vertical' : 'horizontal'}
            series={series}
            loading={loading}
            studyInfo={studyInfo}
            onViewDicomTags={setTagsSeries}
            keyObjects={keyObjects}
            onClearKeyObjects={() => setKeyObjects([])}
          />
        </Box>

        {mprMode ? (
          <MPRView
            studyId={studyInstanceUID}
            seriesId={activeSeriesId || (series[0] && series[0].seriesInstanceUID)}
            ww={mprWindowLevel.ww}
            wc={mprWindowLevel.wc}
            fusionPetSeriesId={fusionPetSeriesId}
            fusionOpacity={fusionOpacity}
            fusionColormap={fusionColormap}
            fusionPetSlabMm={fusionPetSlabMm}
          />
        ) : (
          <Box
            key={layoutKey}
            data-viewport-maximize-mode={maximizedViewportIndex !== null ? VIEWPORT_DOUBLE_CLICK_MAXIMIZE_MARKER : ''}
            data-viewport-slot-isolation={KAIROS_VIEWPORT_SLOT_ISOLATION_V1}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              height: '100%',
              display: 'grid',
              ...(maximizedViewportIndex !== null
                ? { gridTemplateColumns: '1fr', gridTemplateRows: '1fr' }
                : getViewportGridStyle(activeLayout)),
              gap: maximizedViewportIndex !== null ? 0 : '2px',
              bgcolor: '#050505',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {Array.from({ length: numViewports }).map((_, i) => {
              const viewportVisible = isViewportVisibleInMaximizeMode(i, maximizedViewportIndex);
              return (
              <Box
                key={`viewport-slot-${i}`}
                data-testid={`diagnostic-viewport-slot-${i}`}
                data-viewport-slot-visible={viewportVisible ? 'true' : 'false'}
                aria-hidden={viewportVisible ? undefined : true}
                tabIndex={viewportVisible ? undefined : -1}
                sx={viewportVisible ? {
                  display: 'block', width: '100%', height: '100%', minWidth: 0, minHeight: 0,
                  ...(maximizedViewportIndex === null ? getViewportCellStyle(activeLayout, i) : {}),
                } : { position: 'absolute', inset: 0, width: '100%', height: '100%', minWidth: 0, minHeight: 0, visibility: 'hidden', opacity: 0, pointerEvents: 'none' }}
              >
              {i === vrtSlot ? (
              <VRTViewport
                key={`vrt-${i}`}
                studyInstanceUID={studyInstanceUID}
                seriesInstanceUID={activeSeriesId}
                slotLabel={slotLabels[i] || ''}
              />
            ) : (
            <DiagnosticViewportSlot
                key={i}
                index={i}
                initialSeries={preloaded[i] || null}
                toolMode={globalTool}
                initialWL={slotWLs[i] || null}
                slotLabel={slotLabels[i] || ''}
                onSliceChange={onSliceChange}
                onInteractionPerformanceEvent={handleInteractionPerformanceEvent}
                onLinkedSliceRequest={onLinkedSliceRequest}
                onToolComplete={setGlobalTool}
                linkedScrollFraction={linkedScrollFraction(i)}
                linkedScrollCommandId={linkedScrollCommandId(i)}
                linkedScrollCommandChannel={linkedScrollCommandChannel}
                linkedScrollEnabled={linkedScroll}
                linkedAuthorityEpoch={linkedAuthorityEpoch}
                syncCommand={keyboardViewportCommands[i] || externalViewportCommands[i] || triangulationViewportCommands[i] || null}
                publishMultiMonitor={publishMultiMonitor}
                focusReportViewport={focusReportViewport}
                studyInstanceUID={studyInstanceUID}
                setViewportWindowLevels={setViewportWindowLevels}
                setViewportZoomFit={setViewportZoomFit}
                setViewportFreezeState={setViewportFreezeState}
                onReportContextChange={handleReportContextChange}
                handleViewportSeriesLoadedForLinkedScroll={handleViewportSeriesLoadedForLinkedScroll}
                setActiveSeriesId={setActiveSeriesId}
                showRefLines={showRefLines}
                referenceGeometryChannel={referenceGeometryChannel}
                onViewportGeometryChange={handleViewportGeometryChange}
                overlayConfig={overlayConfig}
                annotationAppearance={annotationAppearance}
                onAnnotationAppearanceChange={handleAnnotationAppearanceChange}
                studyInfo={studyInfo}
                comparisonContext={comparisonContext}
                isActiveViewport={i === activeReportViewportIndex}
                isViewportMaximized={maximizedViewportIndex === i}
                viewportLayoutMode={maximizedViewportIndex !== null ? 'maximized' : 'grid'}
                onViewportDoubleClick={handleViewportDoubleClickMaximize}
                keyboardToolShortcuts={keyboardToolShortcuts}
                toolLayoutPrefs={toolLayoutPrefs}
                toolCompletionBehavior={workspacePrefs.toolCompletionBehavior}
                escapeReturnsToScroll={workspacePrefs.escapeReturnsToScroll}
                triangulationPoint={triangulationPoint}
                onTriangulationPoint={handleTriangulationPoint}
                triangulationDragChannel={triangulationDragChannel}
                keyObjects={keyObjects}
                onMarkKeyObject={handleMarkKeyObject}
                cineResourceGovernor={cineResourceGovernor}
                cacheEpoch={cacheEpoch}
                />
            )}
              </Box>
              );
            })}
          </Box>
        )}
        </Box>

        <Box sx={{ width: effectiveReportRailVisible ? (reportOpen ? 390 : 270) : 0, flexShrink: 0, overflow: 'hidden', display: { xs: 'none', lg: effectiveReportRailVisible ? 'flex' : 'none' }, flexDirection: 'column' }}>
          <Box sx={{ height: reportOpen ? 178 : '100%', minHeight: reportOpen ? 178 : 0, flexShrink: 0, overflow: 'hidden' }}>
            <PriorsPanel
              priors={priors}
              loading={priorsLoading}
              error={priorsError}
              compact={reportOpen}
              studyInfo={studyInfo}
              initialSelectedCells={comparisonContext?.cells || EMPTY_COMPARE_CELLS}
              onComparePriors={handleComparePriors}
              onOpenPrior={(uid, options = {}) => {
                if (options.newTab) {
                  window.open(buildPriorCompareStudyUrl(uid, options.compare || {}), '_blank', 'noopener,noreferrer');
                  return;
                }
                navigate(`/study/${uid}`);
              }}
            />
          </Box>
          {reportOpen && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <NativeReportPanel studyInstanceUID={studyInstanceUID} studyInfo={studyInfo} user={user} activeReportContext={activeReportContext} />
            </Box>
          )}
        </Box>
      </Box>
      <Box
        data-kairos-performance-status-bar="RESERVED_NON_DIAGNOSTIC_CHROME"
        sx={{
          flex: '0 0 auto',
          minHeight: 25,
          px: 0.65,
          py: 0.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          borderTop: '1px solid rgba(110,239,255,0.12)',
          bgcolor: '#05080b',
          background: 'linear-gradient(180deg, rgba(5,9,13,0.98), rgba(3,5,7,1))',
        }}
      >
        <StudyPerformanceStatusPill
          report={performanceReport}
          onOpen={() => {
            setPerformanceReport(buildPerformanceReport());
            setPerformanceOpen(true);
          }}
        />
      </Box>
    <KeyboardShortcutsHelp open={showHelp} onClose={() => setShowHelp(false)} />
    <KeyboardShortcutEditorDialog
      open={shortcutEditorOpen}
      preferences={keyboardShortcutPrefs}
      onSave={handleKeyboardShortcutSave}
      onReset={handleKeyboardShortcutReset}
      onClose={() => setShortcutEditorOpen(false)}
    />
    <ViewerToolLayoutEditorDialog
      open={toolLayoutEditorOpen}
      preferences={toolLayoutPrefs}
      onSave={handleToolLayoutSave}
      onClose={() => setToolLayoutEditorOpen(false)}
    />
    <DicomTagsDialog open={!!tagsSeries} onClose={() => setTagsSeries(null)} series={tagsSeries} studyInfo={studyInfo} />
    <OverlayConfigDialog open={overlayDialogOpen} onClose={() => setOverlayDialogOpen(false)} config={overlayConfig} onSave={handleOverlaySave} />
    <HangingProtocolEditorDialog open={hpEditorOpen} onClose={() => setHpEditorOpen(false)} currentProtocol={protocol} overrides={hangingProtocolOverrides} onSave={handleHangingProtocolSave} />
    <QcTicketDialog
      open={qcTicketOpen}
      onClose={() => setQcTicketOpen(false)}
      studyInstanceUID={studyInstanceUID}
      activeReportContext={activeReportContext}
      activeViewportIndex={activeReportViewportIndex || 0}
      protocolLabel={commandDeckStatus.protocolLabel}
      user={user}
    />
    <MultiMonitorDialog open={multiMonitorOpen} onClose={() => setMultiMonitorOpen(false)} studyInstanceUID={studyInstanceUID} {...workspaceParams} />

    </Box>
  );
};

export default ViewerPage;
